import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { KTIcon, toAbsoluteUrl } from 'src/marketConnect/helpers'
import { Navbar } from 'src/marketConnect/layout/components/header/Navbar'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import DOMPurify from 'dompurify'
import { useNavigate } from 'react-router-dom'
import Select, { MultiValue } from 'react-select'
import { Modal, Spinner, Alert, Form, Button, Card } from 'react-bootstrap'
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import AddManageUserFunctionality from '../admin-panel/AddManageUserFunctionality'
import ManageRolesFunctionality from '../admin-panel/ManageRolesFunctionality'
import WarehouseListing from '../warehouse-location/WarehouseListing'
import NewHistoryLogs from '../history-logs/NewHistoryLogs'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import useAppToast from 'src/hooks/useAppToast'
import Loader from 'src/utils/Loader'
import {
  fetchCountriesAsync,
  fetchStatesByCountryAsync,
  fetchUserAccountDetailsAsync,
  generateMfaQrCodeAsync,
  getUserEmailTemplatesAsync,
  deleteUserEmailTemplateAsync,
  getUserInvoiceProfileAsync,
  saveUserInvoiceProfileAsync,
  verifyMfaCodeAsync,
  fetchCurrenciesAsync,
} from 'src/redux/actions/accountActions'
import { updateusernameAsync } from 'src/redux/actions/handelusernamechange'
import {
  getNotificationsSettingsAsync,
  updateNotificationsSettingsAsync,
  updatepasswordAsync,
} from 'src/redux/actions/passwordAction'
import {
  createWarehouseDataAsync,
  getWarehouseListAsync,
  updateWarehouseDetailsAsync,
} from 'src/redux/actions/omsPageActions'
//import {saveCookieConsent as saveCookieConsentAsync} from 'src/redux/actions/cookieConsentActions'
import AppCustomEditor2 from 'src/utils/AppCustomEditor2'
import countries from 'src/utils/CountryCodes'
import { loggedInAdminAccessToken } from 'src/utils/constants'
//import {clearCookieConsent} from 'src/utils/cookieConsent'
import './AccountSettingsModern.scss'
import PackingSlipTemplateTypeSelector from './components/PackingSlipTemplateTypeSelector'
import PackingSlipCustomSettings from './components/PackingSlipCustomSettings'
import PackingSlipCustomWorkspace from './components/PackingSlipCustomWorkspace'
import { getApiErrorMessage } from 'src/marketConnect/helpers'
import {PackingSlipTemplateRecord} from 'src/interfaces/PackingSlipTemplate.interface'
import {
  buildPackingSlipProfilePayloadFromRecord,
  isPackingSlipLegacyStandardTemplateData,
  mapPackingSlipEmailTemplatesListResponse,
  PackingSlipShortcodeValidationState,
  PackingSlipTemplateKindSwitchPlan,
  packingSlipShortcodeSaveBlockMessage,
  planPackingSlipTemplateKindSwitch,
} from 'src/interfaces/packingSlipTemplateMappers'
import {PackingSlipTemplateKind} from 'src/interfaces/PackingSlipTemplate.interface'

type SettingsSectionKey =
  | 'my-account-profile'
  | 'my-account-security'
  | 'my-account-notifications'
  | 'my-account-application-logs'
  | 'company-profile'
  | 'company-branding'
  | 'company-warehouses'
  | 'company-invoice-series'
  | 'user-management-users'
  | 'user-management-roles'
  | 'template-invoice'
  | 'template-packing-slip'

type SettingsMenuItem = {
  key: SettingsSectionKey
  label: string
  icon: string
  breadcrumbLabel: string
  pageTitle: string
}

type SettingsMenuGroup = {
  title: string
  breadcrumbLabel: string
  defaultSection: SettingsSectionKey
  items: SettingsMenuItem[]
}

type SettingsBreadcrumbItem = {
  label: string
  onSelect?: () => void
}

type NotificationCategoryKey =
  | 'isNotifyOrders'
  | 'isNotifyInventory'
  | 'isNotifyPrice'
  | 'isNotifyShipment'
  | 'isNotifyProductCreate'

type NotificationSettingsState = {
  isNotifyOrders: boolean
  isNotifyInventory: boolean
  isNotifyPrice: boolean
  isNotifyShipment: boolean
  isNotifyProductCreate: boolean
  isNotifyEmail: boolean
  isNotifyStaff: boolean
  staff_list: string
}

const NOTIFICATION_CATEGORY_KEYS: NotificationCategoryKey[] = [
  'isNotifyOrders',
  'isNotifyInventory',
  'isNotifyPrice',
  'isNotifyShipment',
  'isNotifyProductCreate',
]

const NOTIFICATION_CATEGORY_ROWS: {
  field: NotificationCategoryKey
  title: string
  description: string
  iconName: string
}[] = [
  {
    field: 'isNotifyOrders',
    title: 'Orders notifications',
    description: 'New orders, cancellations and order sync issues',
    iconName: 'handcart',
  },
  {
    field: 'isNotifyInventory',
    title: 'Inventory notifications',
    description: 'Inventory sync errors',
    iconName: 'cube-3',
  },
  {
    field: 'isNotifyPrice',
    title: 'Pricing notifications',
    description: 'Price changes and price sync issues',
    iconName: 'price-tag',
  },
  {
    field: 'isNotifyShipment',
    title: 'Shipments notifications',
    description: 'Tracking Updates sync errors',
    iconName: 'delivery-3',
  },
  {
    field: 'isNotifyProductCreate',
    title: 'Products & listings notifications',
    description: 'New products and listing sync issues',
    iconName: 'element-11',
  },
]

const toNotifyBoolean = (value: unknown): boolean => Boolean(value)

const mapUserSettingsToNotifications = (
  settings: Record<string, unknown>,
  staffListFallback = ''
): NotificationSettingsState => ({
  isNotifyOrders: toNotifyBoolean(settings?.isNotifyOrders),
  isNotifyInventory: toNotifyBoolean(settings?.isNotifyInventory),
  isNotifyPrice: toNotifyBoolean(settings?.isNotifyPrice),
  isNotifyShipment: toNotifyBoolean(settings?.isNotifyShipment),
  isNotifyProductCreate: toNotifyBoolean(settings?.isNotifyProductCreate),
  isNotifyEmail: toNotifyBoolean(settings?.isNotifyEmail),
  isNotifyStaff: toNotifyBoolean(settings?.isNotifyStaff),
  staff_list: typeof settings?.staff_list === 'string' ? settings.staff_list : staffListFallback,
})

const countEnabledNotificationCategories = (state: NotificationSettingsState): number =>
  NOTIFICATION_CATEGORY_KEYS.filter((key) => state[key]).length

const areAllNotificationCategoriesEnabled = (state: NotificationSettingsState): boolean =>
  countEnabledNotificationCategories(state) === NOTIFICATION_CATEGORY_KEYS.length

const withAllNotificationCategories = (
  state: NotificationSettingsState,
  enabled: boolean
): NotificationSettingsState => ({
  ...state,
  isNotifyOrders: enabled,
  isNotifyInventory: enabled,
  isNotifyPrice: enabled,
  isNotifyShipment: enabled,
  isNotifyProductCreate: enabled,
})

type InvoiceTemplateLineItem = {
  sku: string
  description: string
  quantity: number
  amount: string
  imei: string
}

type InvoiceTemplateRecord = {
  id: string
  templateId?: number | string | null
  name: string
  channels: string[]
  language: string
  includeImei: boolean
  includeWarrantyDetails: boolean
  notes: string
  bankDetails: string
  signatureUrl: string
  isDefault: boolean
  invoiceNumber: string
  invoiceDate: string
  channelLabel: string
  vatNumber: string
  taxNumber: string
  billToName: string
  billToCompany: string
  billToAddress: string
  logoLabel: string
  lineItems: InvoiceTemplateLineItem[]
  templateHtml?: string
  logoUrl?: string
}

type InvoiceChannelOption = {
  value: string
  label: string
}

type LocationOption = {
  value: number
  label: string
  code?: string
}

const resolveAccountSettingsLoggedInUserName = (
  user?: {name?: string | null; email?: string | null} | null
): string => {
  const name = typeof user?.name === 'string' ? user.name.trim() : ''
  if (name) {
    return name
  }

  const email = typeof user?.email === 'string' ? user.email.trim() : ''
  if (email) {
    return email
  }

  return 'User'
}

const menuGroups: SettingsMenuGroup[] = [
  {
    title: 'MY ACCOUNT',
    breadcrumbLabel: 'My account',
    defaultSection: 'my-account-profile',
    items: [
      { key: 'my-account-profile', label: 'Profile', icon: 'profile-circle', breadcrumbLabel: 'My profile', pageTitle: 'My profile' },
      { key: 'my-account-security', label: 'Security', icon: 'lock-2', breadcrumbLabel: 'Security', pageTitle: 'Security' },
      { key: 'my-account-notifications', label: 'Notification Settings', icon: 'notification-on', breadcrumbLabel: 'Notifications', pageTitle: 'Notifications' },
      { key: 'my-account-application-logs', label: 'Application Logs', icon: 'document', breadcrumbLabel: 'Activity log', pageTitle: 'Activity log' },
    ],
  },
  {
    title: 'COMPANY',
    breadcrumbLabel: 'Company',
    defaultSection: 'company-profile',
    items: [
      { key: 'company-profile', label: 'Profile', icon: 'office-bag', breadcrumbLabel: 'Company details', pageTitle: 'Company details' },
      { key: 'company-branding', label: 'Branding', icon: 'color-swatch', breadcrumbLabel: 'Branding', pageTitle: 'Branding' },
      { key: 'company-warehouses', label: 'Warehouses', icon: 'delivery-3', breadcrumbLabel: 'Warehouses', pageTitle: 'Warehouses' },
      { key: 'company-invoice-series', label: 'Invoice Series', icon: 'barcode', breadcrumbLabel: 'Invoice series', pageTitle: 'Invoice series' },
    ],
  },
  {
    title: 'USER MANAGEMENT',
    breadcrumbLabel: 'User management',
    defaultSection: 'user-management-users',
    items: [
      { key: 'user-management-users', label: 'Manage Users', icon: 'people', breadcrumbLabel: 'Manage users', pageTitle: 'Manage users' },
      { key: 'user-management-roles', label: 'Manage Roles', icon: 'security-user', breadcrumbLabel: 'Manage roles', pageTitle: 'Manage roles' },
    ],
  },
  {
    title: 'TEMPLATE EDITOR',
    breadcrumbLabel: 'Template editor',
    defaultSection: 'template-invoice',
    items: [
      { key: 'template-invoice', label: 'Invoice Template', icon: 'file', breadcrumbLabel: 'Invoice template', pageTitle: 'Invoice template' },
      { key: 'template-packing-slip', label: 'Packing Slip Template', icon: 'document', breadcrumbLabel: 'Packing slip template', pageTitle: 'Packing slip template' },
    ],
  },
]

const findSettingsMenuContext = (section: SettingsSectionKey) => {
  for (const group of menuGroups) {
    const item = group.items.find((entry) => entry.key === section)
    if (item) {
      return {group, item}
    }
  }

  const fallbackGroup = menuGroups[0]
  return {group: fallbackGroup, item: fallbackGroup.items[0]}
}

const defaultNotifications: NotificationSettingsState = {
  isNotifyOrders: false,
  isNotifyInventory: false,
  isNotifyPrice: false,
  isNotifyShipment: false,
  isNotifyProductCreate: false,
  isNotifyEmail: false,
  isNotifyStaff: false,
  staff_list: '',
}


// Legacy numeric language_id → code (older profiles may still return 1/2/3)
const languageIdToCode: Record<number, string> = { 1: 'en', 2: 'fr', 3: 'de' }

const resolveLanguageCode = (value: unknown): string => {
  if (value == null || value === '') return 'en'
  const asString = String(value).toLowerCase()
  if (/^[a-z]{2}$/.test(asString)) return asString
  return languageIdToCode[Number(asString)] || 'en'
}

const createEmptyInvoiceTemplate = (): InvoiceTemplateRecord => ({
  id: `new-template-${Date.now()}`,
  templateId: null,
  name: '',
  channels: [],
  language: 'en',
  includeImei: true,
  includeWarrantyDetails: false,
  notes: 'Thank you for your business!',
  bankDetails: 'Deutsche Bank',
  signatureUrl: '',
  isDefault: false,
  invoiceNumber: 'INV-NEW-001',
  invoiceDate: '2026-04-15',
  channelLabel: 'Commercium(OMS)',
  vatNumber: 'DE308847115',
  taxNumber: '',
  billToName: 'Customer Name',
  billToCompany: 'Customer Company',
  billToAddress: 'Business Address',
  logoLabel: 'LOGO',
  lineItems: [
    {
      sku: 'SKU-001',
      description: 'Sample Product',
      quantity: 1,
      amount: '$199.00',
      imei: '354678109928374',
    },
  ],
  logoUrl: '',
})

const PACKING_SLIP_PAGE_FORMATS = {
  '4x6': {
    previewLabel: '4" x 6" THERMAL',
    width: '3.8in',
    height: '5.7in',
  },
  a4: {
    previewLabel: 'A4 STANDARD SHEET PAPER',
    width: '8.27in',
    height: '11.69in',
  },
  letter: {
    previewLabel: '8.5" x 11" LETTER SHEET',
    width: '8.5in',
    height: '11in',
  },

  // Future page formats:
  // legal: {
  //   previewLabel: '8.5" x 14" LEGAL SHEET',
  //   width: '8.5in',
  //   height: '14in',
  // },
  // tabloid: {
  //   previewLabel: '11" x 17" TABLOID SHEET',
  //   width: '11in',
  //   height: '17in',
  // },
  // halfLetter: {
  //   previewLabel: '5.5" x 8.5" HALF LETTER SHEET',
  //   width: '5.5in',
  //   height: '8.5in',
  // },
  // executive: {
  //   previewLabel: '7.25" x 10.5" EXECUTIVE SHEET',
  //   width: '7.25in',
  //   height: '10.5in',
  // },
  // a3: {
  //   previewLabel: 'A3 STANDARD SHEET PAPER',
  //   width: '11.69in',
  //   height: '16.54in',
  // },
  // a5: {
  //   previewLabel: 'A5 STANDARD SHEET PAPER',
  //   width: '5.83in',
  //   height: '8.27in',
  // },
  // a6: {
  //   previewLabel: 'A6 STANDARD SHEET PAPER',
  //   width: '4.13in',
  //   height: '5.83in',
  // },
} as const

const createEmptyPackingSlipTemplate = (): PackingSlipTemplateRecord => ({
  id: `new-packing-slip-${Date.now()}`,
  templateId: null,
  name: '',
  channels: [],
  templateKind: 'standard',
  paperSize: '4x6',
  displayBrandLogo: true,
  displayProductImages: true,
  displayShippingMethod: true,
  displayCustomerNotes: true,
  displayWarehouseBins: true,
  isDefault: false,
  templateHtml: '',
  logoUrl: '',
  marginMm: 0,
  orientation: 'portrait',
  pdfFileName: '',
  repeatTableHeader: true,
  blockSavingUnknownSourceCode: true,
})

const unwrapListResponse = (payload: any) => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (Array.isArray(payload?.results)) {
    return payload.results
  }

  if (Array.isArray(payload?.results?.data)) {
    return payload.results.data
  }

  if (Array.isArray(payload?.data)) {
    return payload.data
  }

  return []
}

const unwrapWarehouseResponse = (payload: any) => {
  if (Array.isArray(payload?.results?.data)) {
    return payload.results.data
  }

  return unwrapListResponse(payload)
}

const getOptionLabel = (item: any) => item?.name || item?.country || item?.state || item?.label || ''

const getOptionValue = (item: any) => Number(item?.id ?? item?.value ?? 0)

const toLocationOptions = (payload: any): LocationOption[] =>
  unwrapListResponse(payload)
    .map((item: any) => ({
      value: getOptionValue(item),
      label: getOptionLabel(item),
      code: item?.code,
    }))
    .filter((item: LocationOption) => item.value && item.label)

const firstSavedValue = (...values: any[]) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== '')

const toSavedId = (value: any) => {
  const id = Number(value)
  return Number.isFinite(id) && id > 0 ? id : null
}

const parseAdditionalData = (value: any) => {
  if (!value) {
    return {}
  }

  if (typeof value === 'object') {
    return value
  }

  try {
    return JSON.parse(value)
  } catch {
    return {}
  }
}

const normalizeProfileResponse = (data: any) => {
  if (!data) {
    return null
  }

  if (data?.profile || data?.template || data?.user) {
    return data
  }

  if (data?.data) {
    return normalizeProfileResponse(data.data)
  }

  if (data?.results) {
    return normalizeProfileResponse(data.results)
  }

  return data
}

const fileToDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

const resolveInvoiceProfileData = (data: any) => {
  data = normalizeProfileResponse(data)

  if (!data) {
    return null
  }

  if (data?.invoice_profile) {
    return data.invoice_profile
  }

  if (data?.profile) {
    return data.profile
  }

  if (data?.user_profile?.type === 'INVOICE') {
    return data.user_profile
  }

  if (Array.isArray(data?.user_profile)) {
    return data.user_profile.find((item: any) => item?.type === 'INVOICE') || null
  }

  if (Array.isArray(data?.user_profiles)) {
    return data.user_profiles.find((item: any) => item?.type === 'INVOICE') || null
  }

  return data?.type === 'INVOICE' ? data : null
}

const resolveProfileTemplates = (data: any) => {
  data = normalizeProfileResponse(data)

  const templateSource =
    data?.template ??
    data?.templates ??
    data?.invoice_templates ??
    data?.template_data ??
    data?.profile?.template ??
    data?.profile?.templates ??
    data?.profile?.template_data

  return Array.isArray(templateSource) ? templateSource : templateSource ? [templateSource] : []
}

const resolveInvoiceTemplatesData = (data: any, profileData?: any): InvoiceTemplateRecord[] => {
  data = normalizeProfileResponse(data)

  const rawTemplates = resolveProfileTemplates(data)
  const profile = profileData || resolveInvoiceProfileData(data) || {}
  const invoiceNumber = `${profile?.sales_order_number_prefix || 'INV-'}${profile?.sales_order_number || '1'}`
  const invoiceDate = (profile?.updated_at || profile?.created_at || new Date().toISOString()).slice(0, 10)

  return rawTemplates.map((template: any, index: number) => {
    const additionalData = parseAdditionalData(template?.additional_data)
    const payment = template?.payment || additionalData?.payment || {}
    const language = resolveLanguageCode(
      template?.language_id ?? additionalData?.language_id ?? profile?.language_id ?? template?.language ?? 'en'
    )
    const channelLabel = template?.platform?.name || template?.platform_name || 'Commercium(OMS)'
    const logoUrl = firstSavedValue(
      profile?.brand_logo,
      profile?.logo,
      template?.brand_logo,
      template?.logo,
      additionalData?.brand_logo,
      additionalData?.logo
    ) || ''
    const vatNumber = firstSavedValue(profile?.vat_id, profile?.vat_number, template?.vat_id, additionalData?.vat_id) || ''
    const taxNumber = firstSavedValue(profile?.tax_number, profile?.tax_id, template?.tax_number, template?.tax_id, additionalData?.tax_number, additionalData?.tax_id) || ''

    return {
      id: String(template?.id ?? `template-${index}`),
      templateId: template?.template_id ?? template?.id ?? null,
      name: template?.name || `Invoice Template #${template?.id ?? index + 1}`,
      channels: template?.platform_ids?.length ? template.platform_ids.map(String) : (template?.platform ? [String(template.platform)] : []),
      language,
      includeImei: Boolean(template?.include_imei ?? additionalData?.include_imei),
      includeWarrantyDetails: Boolean(template?.include_warranty ?? additionalData?.include_warranty),
      notes: template?.notes || '',
      bankDetails: payment?.bank_detail || '',
      signatureUrl: template?.signature_url || additionalData?.signature_url || '',
      isDefault: Boolean(template?.is_default),
      invoiceNumber,
      invoiceDate,
      channelLabel,
      vatNumber,
      taxNumber,
      billToName: '',
      billToCompany: '',
      billToAddress: '',
      logoLabel: logoUrl ? '' : 'LOGO',
      lineItems: [],
      templateHtml: template?.template_data || '',
      logoUrl,
    }
  })
}

const resolvePackingSlipTemplatesData = (data: any): PackingSlipTemplateRecord[] =>
  mapPackingSlipEmailTemplatesListResponse(data)

const resolveBrandLogo = (data: any) => {
  data = normalizeProfileResponse(data)

  const profile = resolveInvoiceProfileData(data) || {}
  const templates = resolveProfileTemplates(data)
  const defaultTemplate = templates.find((template: any) => template?.is_default) || templates[0]
  const additionalData = parseAdditionalData(defaultTemplate?.additional_data)

  return (
    firstSavedValue(
      profile?.brand_logo,
      profile?.logo,
      defaultTemplate?.brand_logo,
      defaultTemplate?.logo,
      additionalData?.brand_logo,
      additionalData?.logo
    ) || ''
  )
}

const resolveTaxNumber = (data: any) => {
  data = normalizeProfileResponse(data)

  const profile = resolveInvoiceProfileData(data) || {}
  const templates = resolveProfileTemplates(data)
  const defaultTemplate = templates.find((template: any) => template?.is_default) || templates[0]
  const additionalData = parseAdditionalData(defaultTemplate?.additional_data)
  const additionalInfo = data?.userAdditionalInfo || {}

  return (
    firstSavedValue(
      profile?.tax_number,
      profile?.tax_id,
      defaultTemplate?.tax_number,
      defaultTemplate?.tax_id,
      additionalData?.tax_number,
      additionalData?.tax_id,
      additionalInfo?.tax_number,
      additionalInfo?.tax_id
    ) || ''
  )
}

const resolveCompanyAddressData = (data: any) => {
  data = normalizeProfileResponse(data)

  const invoiceProfile = resolveInvoiceProfileData(data)
  const address = invoiceProfile?.address || {}
  const additionalInfo = data?.userAdditionalInfo || {}

  return {
    street1: firstSavedValue(address?.street1, invoiceProfile?.street1, additionalInfo?.street1, additionalInfo?.address) || '',
    street2: firstSavedValue(address?.street2, invoiceProfile?.street2, additionalInfo?.street2) || '',
    zip: firstSavedValue(address?.zip, address?.zip_code, invoiceProfile?.zip, invoiceProfile?.zip_code, additionalInfo?.zip, additionalInfo?.zip_code) || '',
    city: firstSavedValue(address?.city, invoiceProfile?.city, additionalInfo?.city) || '',
    countryId: toSavedId(
      firstSavedValue(
        address?.country_id,
        address?.country?.id,
        invoiceProfile?.country_id,
        invoiceProfile?.country?.id,
        invoiceProfile?.country,
        invoiceProfile?.organization_country,
        invoiceProfile?.orgnization_country,
        additionalInfo?.country_id
      )
    ),
    stateId: toSavedId(
      firstSavedValue(address?.state_id, address?.state?.id, invoiceProfile?.state_id, invoiceProfile?.state?.id, invoiceProfile?.state, additionalInfo?.state_id)
    ),
    countryName: firstSavedValue(
      address?.country?.name,
      invoiceProfile?.country_name,
      invoiceProfile?.organization_country_name,
      invoiceProfile?.orgnization_country_name,
      address?.country,
      invoiceProfile?.country?.name,
      additionalInfo?.country
    ),
    stateName: firstSavedValue(address?.state?.name, invoiceProfile?.state_name, address?.state, invoiceProfile?.state?.name, additionalInfo?.state),
  }
}

const AccountSettingsModern = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { successToast, errorToast, warningToast } = useAppToast()
  const { fetchedUserAccountData, fetchedUserStaffData } = useAppSelector((state) => state.userAccountData)
  const { omsStoreData } = useAppSelector((state) => state.fetchConnectedPlatform)
  const hasOmsPlatform = Boolean(omsStoreData)
  // const {consent: cookieConsent, hydrated: cookieConsentHydrated, saving: cookieConsentSaving} =
  //   useAppSelector((state) => state.cookieConsent)
  const isLoginAdminAccess = Boolean(localStorage.getItem(loggedInAdminAccessToken))
  const canAccessApplicationLogs =
    fetchedUserStaffData?.account_type === 'ADMIN_STAFF' || isLoginAdminAccess

  const settingsLoggedInUserName = useMemo(
    () => resolveAccountSettingsLoggedInUserName(fetchedUserAccountData),
    [fetchedUserAccountData]
  )

  const [activeSection, setActiveSection] = useState<SettingsSectionKey>('my-account-profile')

  const goToDashboard = useCallback(() => {
    navigate('/dashboard')
  }, [navigate])

  const {group: activeMenuGroup, item: activeMenuItem} = useMemo(
    () => findSettingsMenuContext(activeSection),
    [activeSection]
  )

  const settingsBreadcrumbItems = useMemo<SettingsBreadcrumbItem[]>(
    () => [
      {label: 'Dashboard', onSelect: goToDashboard},
      {label: 'Settings', onSelect: () => setActiveSection('my-account-profile')},
      {
        label: activeMenuGroup.breadcrumbLabel,
        onSelect: () => setActiveSection(activeMenuGroup.defaultSection),
      },
      {label: activeMenuItem.breadcrumbLabel},
    ],
    [activeMenuGroup, activeMenuItem, goToDashboard]
  )

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<any>(countries.find((c) => c.code === 'IN'))

  const [countrySearchQuery, setCountrySearchQuery] = useState('')
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false)

  const [companyName, setCompanyName] = useState('')
  const [gstNumber, setGstNumber] = useState('')
  const [taxId, setTaxId] = useState('')
  const [companyStreet1, setCompanyStreet1] = useState('')
  const [companyStreet2, setCompanyStreet2] = useState('')
  const [companyZip, setCompanyZip] = useState('')
  const [companyCity, setCompanyCity] = useState('')
  const [companyCountryId, setCompanyCountryId] = useState<number | null>(null)
  const [companyStateId, setCompanyStateId] = useState<number | null>(null)
  const [companyCountryOptions, setCompanyCountryOptions] = useState<LocationOption[]>([])
  const [companyStateOptions, setCompanyStateOptions] = useState<LocationOption[]>([])
  const [isLoadingCountries, setIsLoadingCountries] = useState(false)
  const [isLoadingStates, setIsLoadingStates] = useState(false)
  const [brandLogo, setBrandLogo] = useState('')
  const [brandLogoFileName, setBrandLogoFileName] = useState('')
  const [warehouseId, setWarehouseId] = useState<number | null>(null)
  const [primaryWarehouse, setPrimaryWarehouse] = useState('')
  const [warehouseContact, setWarehouseContact] = useState('')
  const [warehouseAddress1, setWarehouseAddress1] = useState('')
  const [warehouseAddress2, setWarehouseAddress2] = useState('')
  const [warehouseCity, setWarehouseCity] = useState('')
  const [warehouseState, setWarehouseState] = useState('')
  const [warehouseStateId, setWarehouseStateId] = useState<number | null>(null)
  const [warehouseStateOptions, setWarehouseStateOptions] = useState<LocationOption[]>([])
  const [warehouseCountry, setWarehouseCountry] = useState('')
  const [warehouseCountryId, setWarehouseCountryId] = useState<number | null>(null)
  const [invoicePrefix, setInvoicePrefix] = useState('INV-')
  const [invoiceStartingNumber, setInvoiceStartingNumber] = useState('000001')

  const [currencyOptions, setCurrencyOptions] = useState<any[]>([])
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState<string>('')
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false)

  const [previousPassword, setPreviousPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordErrors, setPasswordErrors] = useState<{ previous?: string; new?: string; confirm?: string }>({})
  const [showPrevPassword, setShowPrevPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const [notifications, setNotifications] = useState<NotificationSettingsState>(defaultNotifications)
  const [savedNotificationSettings, setSavedNotificationSettings] =
    useState<NotificationSettingsState>(defaultNotifications)
  const [savedInvoiceProfile, setSavedInvoiceProfile] = useState<any>(null)

  const enabledNotificationCategoryCount = countEnabledNotificationCategories(notifications)
  const allNotificationCategoriesEnabled = areAllNotificationCategoriesEnabled(notifications)

  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [isSavingCompany, setIsSavingCompany] = useState(false)
  const [isSavingPassword, setIsSavingPassword] = useState(false)
  const [isSavingNotifications, setIsSavingNotifications] = useState(false)
  const [isUpdatingMfa, setIsUpdatingMfa] = useState(false)
  const [isSavingInvoiceSeries, setIsSavingInvoiceSeries] = useState(false)
  const [isSavingInvoiceTemplate, setIsSavingInvoiceTemplate] = useState(false)
  const [isSavingBranding, setIsSavingBranding] = useState(false)
  const [isRemovingLogo, setIsRemovingLogo] = useState(false)
  const [apiLanguages, setApiLanguages] = useState<{ code: string; name: string }[]>([])
  const [apiSecondaryPlatforms, setApiSecondaryPlatforms] = useState<{ id: number; name: string; display_name: string }[]>([])
  const [isLoadingWarehouse, setIsLoadingWarehouse] = useState(false)
  const [isSavingWarehouse, setIsSavingWarehouse] = useState(false)
  const [isLoadingWarehouseStates, setIsLoadingWarehouseStates] = useState(false)
  const [invoiceTemplates, setInvoiceTemplates] = useState<InvoiceTemplateRecord[]>([])
  const [editingInvoiceTemplate, setEditingInvoiceTemplate] = useState<InvoiceTemplateRecord | null>(null)
  const [packingSlipTemplates, setPackingSlipTemplates] =
    useState<PackingSlipTemplateRecord[]>([])

  const [editingPackingSlipTemplate, setEditingPackingSlipTemplate] =
    useState<PackingSlipTemplateRecord | null>(null)
  const [showDeletePackingSlipModal, setShowDeletePackingSlipModal] =
    useState(false)
  const [packingSlipTemplateToDelete, setPackingSlipTemplateToDelete] =
    useState<PackingSlipTemplateRecord | null>(null)
  const [isDeletingPackingSlipTemplate, setIsDeletingPackingSlipTemplate] =
    useState(false)

  const [isSavingPackingSlipTemplate, setIsSavingPackingSlipTemplate] =
    useState(false)
  const [customPackingSlipShortcodeValidation, setCustomPackingSlipShortcodeValidation] =
    useState<PackingSlipShortcodeValidationState>({
      unknownTokens: [],
      catalogLoaded: false,
      catalogCheckComplete: false,
    })
  const [packingSlipKindSwitchPrompt, setPackingSlipKindSwitchPrompt] =
    useState<PackingSlipTemplateKindSwitchPlan | null>(null)
  const mfaEnabled = Boolean(fetchedUserAccountData?.userAdditionalInfo?.mfa_enabled)
  const { qrCodeUrl, loading: qrLoading } = useAppSelector((state) => state.mfaQrCode)
  const hasMfaQrCode = typeof qrCodeUrl === 'string' ? qrCodeUrl.length !== 0 : Array.isArray(qrCodeUrl) ? qrCodeUrl.length !== 0 : Boolean(qrCodeUrl)
  const [showMfaSetupModal, setShowMfaSetupModal] = useState(false)
  const [showDisableMfaModal, setShowDisableMfaModal] = useState(false)

  const MfaSchema = Yup.object().shape({
    mfaCode: Yup.string()
      .required('Authentication code is required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed')
      .length(6, 'Authentication code must be 6 digits'),
  })
  const DisableMfaSchema = Yup.object().shape({
    disablecode: Yup.string()
      .required('Authentication code is required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed')
      .length(6, 'Authentication code must be 6 digits'),
  })

  const normalizedPhone = useMemo(() => {
    const clean = (phoneNumber || '').trim()
    if (!clean) {
      return ''
    }

    if (clean.startsWith('+')) {
      return clean
    }

    const dial = selectedCountry?.dial_code || ''
    return `${dial}${clean}`
  }, [phoneNumber, selectedCountry])

  const invoiceSeriesPreview = useMemo(
    () => `${invoicePrefix}${invoiceStartingNumber || '000001'}`,
    [invoicePrefix, invoiceStartingNumber]
  )

  const accountDataWithInvoiceProfile = useMemo(() => {
    const normalizedSavedProfile = normalizeProfileResponse(savedInvoiceProfile)

    if (!fetchedUserAccountData || !normalizedSavedProfile) {
      return fetchedUserAccountData
    }

    return {
      ...fetchedUserAccountData,
      ...(normalizedSavedProfile?.user || {}),
      userAdditionalInfo: normalizedSavedProfile?.user?.userAdditionalInfo || fetchedUserAccountData?.userAdditionalInfo,
      invoice_profile: resolveInvoiceProfileData(normalizedSavedProfile) || normalizedSavedProfile,
      template:
        normalizedSavedProfile?.template ??
        normalizedSavedProfile?.templates ??
        normalizedSavedProfile?.invoice_templates ??
        normalizedSavedProfile?.template_data ??
        normalizedSavedProfile?.profile?.template ??
        normalizedSavedProfile?.profile?.templates ??
        normalizedSavedProfile?.profile?.template_data,
    }
  }, [fetchedUserAccountData, savedInvoiceProfile])

  const selectedCompanyCountry = useMemo(
    () => companyCountryOptions.find((country) => country.value === companyCountryId) || null,
    [companyCountryId, companyCountryOptions]
  )

  const selectedCompanyState = useMemo(
    () => companyStateOptions.find((state) => state.value === companyStateId) || null,
    [companyStateId, companyStateOptions]
  )

  const selectedWarehouseCountry = useMemo(
    () => companyCountryOptions.find((country) => country.value === warehouseCountryId) || null,
    [companyCountryOptions, warehouseCountryId]
  )

  const selectedWarehouseState = useMemo(
    () => warehouseStateOptions.find((state) => state.value === warehouseStateId) || null,
    [warehouseStateId, warehouseStateOptions]
  )

  const legacyCompanyAddress = useMemo(
    () =>
      [companyStreet1, companyStreet2, companyZip, companyCity, selectedCompanyState?.label, selectedCompanyCountry?.label]
        .filter(Boolean)
        .join(', '),
    [companyStreet1, companyStreet2, companyZip, companyCity, selectedCompanyState, selectedCompanyCountry]
  )

  const buildCompanyAddressPayload = () => ({
    street1: companyStreet1,
    street2: companyStreet2,
    zip: companyZip,
    city: companyCity,
    state_id: companyStateId,
    country_id: companyCountryId,
  })

  useEffect(() => {
    dispatch(fetchUserAccountDetailsAsync(undefined))
  }, [dispatch])

  useEffect(() => {
    if (!fetchedUserAccountData?.id) {
      setSavedInvoiceProfile(null)
      setPackingSlipTemplates([])
      return
    }

    dispatch(getUserInvoiceProfileAsync({ userId: fetchedUserAccountData.id }) as any)
      .then((response: any) => {
        setSavedInvoiceProfile(normalizeProfileResponse(response?.payload) || null)
        if (response?.payload?.languages?.length) {
          setApiLanguages(response.payload.languages)
        }
        if (response?.payload?.secondary_platforms?.length) {
          setApiSecondaryPlatforms(response.payload.secondary_platforms)
        }
      })
      .catch(() => {
        setSavedInvoiceProfile(null)
      })

    dispatch(getUserEmailTemplatesAsync({ userId: fetchedUserAccountData.id, type: 'PACKING_SLIP' }) as any)
      .then((response: any) => {
        setPackingSlipTemplates(resolvePackingSlipTemplatesData(response?.payload))
      })
      .catch(() => {
        setPackingSlipTemplates([])
      })
  }, [dispatch, fetchedUserAccountData?.id])

  useEffect(() => {
    setIsLoadingCountries(true)
    dispatch(fetchCountriesAsync() as any)
      .then((response: any) => {
        setCompanyCountryOptions(toLocationOptions(response?.payload))
      })
      .catch(() => {
        setCompanyCountryOptions([])
      })
      .finally(() => setIsLoadingCountries(false))
  }, [dispatch])

  useEffect(() => {
    if (!companyCountryId) {
      setCompanyStateOptions([])
      setCompanyStateId(null)
      return
    }

    setIsLoadingStates(true)
    dispatch(fetchStatesByCountryAsync({ countryId: companyCountryId }) as any)
      .then((response: any) => {
        const options = toLocationOptions(response?.payload)
        setCompanyStateOptions(options)
        setCompanyStateId((current) => (current && options.some((state) => state.value === current) ? current : null))
      })
      .catch(() => {
        setCompanyStateOptions([])
        setCompanyStateId(null)
      })
      .finally(() => setIsLoadingStates(false))
  }, [companyCountryId, dispatch])

  useEffect(() => {
    if (!warehouseCountryId) {
      setWarehouseStateOptions([])
      setWarehouseStateId(null)
      return
    }

    setIsLoadingWarehouseStates(true)
    dispatch(fetchStatesByCountryAsync({ countryId: warehouseCountryId }) as any)
      .then((response: any) => {
        const options = toLocationOptions(response?.payload)
        setWarehouseStateOptions(options)
        setWarehouseStateId((current) => (current && options.some((state) => state.value === current) ? current : null))
      })
      .catch(() => {
        setWarehouseStateOptions([])
        setWarehouseStateId(null)
      })
      .finally(() => setIsLoadingWarehouseStates(false))
  }, [dispatch, warehouseCountryId])

  useEffect(() => {
    setIsLoadingCurrencies(true)
    dispatch(fetchCurrenciesAsync() as any)
      .unwrap()
      .then((res: any) => {
        const data = res?.payload || res?.data || res || []
        const list = Array.isArray(data) ? data : (data?.results?.data || data?.data || [])
        const options = list.map((curr: any) => ({
          value: curr.currency_code,
          label: `${curr.currency_code} - ${curr.currency_symbol}`,
          name: curr.currency_name
        }))
        setCurrencyOptions(options)
      })
      .catch((e: any) => console.error('Error fetching currencies:', e))
      .finally(() => setIsLoadingCurrencies(false))
  }, [dispatch])


  const loadPrimaryWarehouse = useCallback(async () => {
    setIsLoadingWarehouse(true)
    try {
      const response: any = await dispatch(
        getWarehouseListAsync({
          paramData: {
            page: 1,
            limit: 1,
            search: '',
            sort_by: 'id',
            sort_order: 'asc',
            warehouse_id: null,
            isActive: true,
            include: 'address,additional_data',
          },
        }) as any
      ).unwrap()

      const warehouse = unwrapWarehouseResponse(response)[0]
      if (!warehouse) {
        setWarehouseId(null)
        setPrimaryWarehouse('')
        setWarehouseContact('')
        setWarehouseAddress1('')
        setWarehouseAddress2('')
        setWarehouseCity('')
        setWarehouseState('')
        setWarehouseStateId(null)
        setWarehouseStateOptions([])
        setWarehouseCountry('')
        setWarehouseCountryId(null)
        return
      }

      const address = warehouse?.address || {}
      setWarehouseId(warehouse?.id ?? null)
      setPrimaryWarehouse(warehouse?.api_name || '')
      setWarehouseContact(address?.phone || '')
      setWarehouseAddress1(address?.address_1 || '')
      setWarehouseAddress2(address?.address_2 || '')
      setWarehouseCity(address?.city || '')
      setWarehouseState(address?.state || '')
      setWarehouseStateId(null)
      setWarehouseCountry(address?.country || '')
      setWarehouseCountryId(null)
    } catch {
      setWarehouseId(null)
    } finally {
      setIsLoadingWarehouse(false)
    }
  }, [dispatch])

  useEffect(() => {
    loadPrimaryWarehouse()
  }, [loadPrimaryWarehouse])

  useEffect(() => {
    if (warehouseCountryId || !warehouseCountry || !companyCountryOptions.length) {
      return
    }

    const matchedCountry = companyCountryOptions.find(
      (country) => country.label.toLowerCase() === warehouseCountry.toLowerCase()
    )
    if (matchedCountry) {
      setWarehouseCountryId(matchedCountry.value)
    }
  }, [companyCountryOptions, warehouseCountry, warehouseCountryId])

  useEffect(() => {
    if (warehouseStateId || !warehouseState || !warehouseStateOptions.length) {
      return
    }

    const matchedState = warehouseStateOptions.find((state) => state.label.toLowerCase() === warehouseState.toLowerCase())
    if (matchedState) {
      setWarehouseStateId(matchedState.value)
    }
  }, [warehouseState, warehouseStateId, warehouseStateOptions])

  useEffect(() => {
    if (companyCountryId || !companyCountryOptions.length) {
      return
    }

    const data: any = accountDataWithInvoiceProfile
    const addressData = resolveCompanyAddressData(data)
    const countryName = addressData.countryName
    if (!countryName) {
      return
    }

    const matchedCountry = companyCountryOptions.find(
      (country) => country.label.toLowerCase() === String(countryName).toLowerCase()
    )
    if (matchedCountry) {
      setCompanyCountryId(matchedCountry.value)
    }
  }, [accountDataWithInvoiceProfile, companyCountryId, companyCountryOptions])

  useEffect(() => {
    if (companyStateId || !companyStateOptions.length) {
      return
    }

    const data: any = accountDataWithInvoiceProfile
    const addressData = resolveCompanyAddressData(data)
    const stateName = addressData.stateName
    if (!stateName) {
      return
    }

    const matchedState = companyStateOptions.find((state) => state.label.toLowerCase() === String(stateName).toLowerCase())
    if (matchedState) {
      setCompanyStateId(matchedState.value)
    }
  }, [accountDataWithInvoiceProfile, companyStateId, companyStateOptions])

  useEffect(() => {
    const data: any = accountDataWithInvoiceProfile
    if (!data) {
      return
    }

    const invoiceProfile = resolveInvoiceProfileData(data)
    setFullName(data?.name || '')
    setEmail(data?.email || '')
    setCompanyName(invoiceProfile?.company_name || data?.company_name || '')
    setGstNumber(firstSavedValue(invoiceProfile?.vat_id, invoiceProfile?.vat_number, data?.gst_number) || '')
    setTaxId(resolveTaxNumber(data))
    setSelectedCurrencyCode(data?.currency_code || data?.userAdditionalInfo?.currency_code || invoiceProfile?.currency_code || '')
    const addressData = resolveCompanyAddressData(data)
    setCompanyStreet1(addressData.street1)
    setCompanyStreet2(addressData.street2)
    setCompanyZip(addressData.zip)
    setCompanyCity(addressData.city)
    setCompanyCountryId(addressData.countryId)
    setCompanyStateId(addressData.stateId)
    setBrandLogo(resolveBrandLogo(data))
    setBrandLogoFileName('')
    if (invoiceProfile) {
      setInvoicePrefix(invoiceProfile?.sales_order_number_prefix || 'INV-')
      setInvoiceStartingNumber(String(invoiceProfile?.sales_order_number ?? '000001'))
      setGstNumber(firstSavedValue(invoiceProfile?.vat_id, invoiceProfile?.vat_number, data?.gst_number) || '')
      setTaxId(resolveTaxNumber(data))
    }

    setInvoiceTemplates(resolveInvoiceTemplatesData(data, invoiceProfile))

    const countryByName = countries.find(
      (country) => country.name.toUpperCase() === (data?.userAdditionalInfo?.country || '').toUpperCase()
    )
    const resolvedCountry = countryByName || countries.find((country) => country.code === 'IN')
    setSelectedCountry(resolvedCountry)

    const rawPhone = (data?.userAdditionalInfo?.phone_number || '').toString()
    const phoneWithoutSpaces = rawPhone.replace(/\s+/g, '')
    if (resolvedCountry?.dial_code && phoneWithoutSpaces.startsWith(resolvedCountry.dial_code)) {
      setPhoneNumber(phoneWithoutSpaces.slice(resolvedCountry.dial_code.length).replace(/\D/g, ''))
    } else {
      setPhoneNumber(rawPhone.replace(/\D/g, ''))
    }

    if (data?.id) {
      dispatch(getNotificationsSettingsAsync({ user_id: data.id }))
        .then((res: any) => {
          const settings = res?.payload?.user_settings || {}
          const mapped = mapUserSettingsToNotifications(settings)

          setNotifications(mapped)
          setSavedNotificationSettings(mapped)
        })
        .catch(() => {
          setNotifications((prev) => ({ ...prev, staff_list: data?.email || '' }))
        })
    }
  }, [accountDataWithInvoiceProfile, dispatch])

    useEffect(() => {
    if (activeSection !== 'template-invoice' && editingInvoiceTemplate) {
      setEditingInvoiceTemplate(null)
    }
  }, [activeSection, editingInvoiceTemplate])

  const saveProfileDetails = () => {
    if (!fullName.trim()) {
      errorToast('Name cannot be empty')
      return
    }

    if (phoneNumber && !/^\d+$/.test(phoneNumber)) {
      errorToast('Phone number must contain digits only')
      return
    }

    setIsSavingProfile(true)
    dispatch(
      updateusernameAsync({
        name: fullName,
        companyName,
        gstNumber,
        taxId,
        tax_id: taxId,
        tax_number: taxId,
        phoneNumber: normalizedPhone,
        countryCode: selectedCountry?.code,
      }) as any
    )
      .then(async () => {
        successToast('Profile changes saved successfully')
        await dispatch(fetchUserAccountDetailsAsync(undefined))
      })
      .catch(() => {
        errorToast('Unable to save profile changes')
      })
      .finally(() => setIsSavingProfile(false))
  }

  const saveCompanyDetails = () => {
    const userId = fetchedUserAccountData?.id

    if (!userId) {
      errorToast('User account details are not available yet')
      return
    }

    if (!companyName.trim()) {
      errorToast('Company name cannot be empty')
      return
    }

    if (phoneNumber && !/^\d+$/.test(phoneNumber)) {
      errorToast('Phone number must contain digits only')
      return
    }

    if (!companyCountryId) {
      errorToast('Country is required')
      return
    }

    setIsSavingCompany(true)
    dispatch(
      updateusernameAsync({
        name: fullName,
        companyName,
        gstNumber,
        taxId,
        tax_id: taxId,
        tax_number: taxId,
        phoneNumber: normalizedPhone,
        countryCode: selectedCountry?.code,
        address: legacyCompanyAddress,
      }) as any
    )
      .then(async () => {
        await syncInvoiceProfileSettings()
        successToast('Company details saved successfully')
        await dispatch(fetchUserAccountDetailsAsync(undefined))
        const profileResponse = await dispatch(getUserInvoiceProfileAsync({ userId }) as any)
        setSavedInvoiceProfile(normalizeProfileResponse(profileResponse?.payload) || null)
      })
      .catch(() => {
        errorToast('Unable to save company details')
      })
      .finally(() => setIsSavingCompany(false))
  }

  const savePassword = () => {
    const errors: { previous?: string; new?: string; confirm?: string } = {}
    if (!previousPassword) errors.previous = 'Enter your current password'
    if (!newPassword) errors.new = 'Password is required'
    else {
      const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/
      if (!strongPasswordRegex.test(newPassword))
        errors.new = 'Use 8+ chars with uppercase, lowercase, number and special character'
    }
    if (!confirmPassword) errors.confirm = 'Confirm password is required'
    else if (newPassword && confirmPassword !== newPassword) errors.confirm = 'Passwords do not match'

    if (Object.keys(errors).length) {
      setPasswordErrors(errors)
      return
    }
    setPasswordErrors({})
    setIsSavingPassword(true)
    dispatch(
      updatepasswordAsync({
        previous_password: previousPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }) as any
    )
      .then((response: any) => {
        if (response?.payload?.status_code === 200) {
          successToast(response?.payload?.massage || 'Password updated successfully')
          setPreviousPassword('')
          setNewPassword('')
          setConfirmPassword('')
        } else {
          errorToast('Previous password is incorrect. Please try again.')
        }
      })
      .catch(() => {
        errorToast('Unable to update password')
      })
      .finally(() => setIsSavingPassword(false))
  }

  const saveNotificationSettings = () => {
    if (!fetchedUserAccountData?.id || isSavingNotifications) {
      return
    }

    const stateSnapshot = { ...notifications }
    const apiPayload = {
      ...stateSnapshot,
      isNotifyOrders: stateSnapshot.isNotifyOrders ? 1 : 0,
      isNotifyInventory: stateSnapshot.isNotifyInventory ? 1 : 0,
      isNotifyPrice: stateSnapshot.isNotifyPrice ? 1 : 0,
      isNotifyShipment: stateSnapshot.isNotifyShipment ? 1 : 0,
      isNotifyProductCreate: stateSnapshot.isNotifyProductCreate ? 1 : 0,
    }

    setIsSavingNotifications(true)
    dispatch(
      updateNotificationsSettingsAsync({
        user_id: fetchedUserAccountData.id,
        payload: apiPayload,
      }) as any
    )
      .then((response: any) => {
        if (response?.payload?.data?.id || response?.payload?.status_code === 200) {
          successToast(response?.payload?.message || 'Notification settings updated')
          setSavedNotificationSettings(stateSnapshot)
        } else {
          warningToast('Notification settings updated')
        }
      })
      .catch(() => {
        errorToast('Unable to update notification settings')
      })
      .finally(() => setIsSavingNotifications(false))
  }

  const cancelNotificationSettingsEdits = () => {
    setNotifications({ ...savedNotificationSettings })
  }

  const resetNotificationCategoriesToDefault = () => {
    setNotifications((prev) => withAllNotificationCategories(prev, true))
  }

  const setMasterNotificationCategories = (enabled: boolean) => {
    setNotifications((prev) => withAllNotificationCategories(prev, enabled))
  }

  const setNotificationCategoryEnabled = (field: NotificationCategoryKey, enabled: boolean) => {
    setNotifications((prev) => ({ ...prev, [field]: enabled }))
  }

  const refreshInvoiceProfile = async (userId: number) => {
    const profileResponse = await dispatch(getUserInvoiceProfileAsync({ userId }) as any)
    const normalizedProfile = normalizeProfileResponse(profileResponse?.payload) || null
    setSavedInvoiceProfile(normalizedProfile)
    return normalizedProfile
  }

  const handleBrandLogoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file) {
      return
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
      warningToast('Please upload JPG, PNG, or GIF only')
      return
    }

    try {
      const dataUrl = await fileToDataUrl(file)
      setBrandLogo(dataUrl)
      setBrandLogoFileName(file.name)
    } catch {
      errorToast('Unable to read selected logo')
    }
  }

  const saveBrandingSettings = async () => {
    const userId = fetchedUserAccountData?.id

    if (!userId) {
      errorToast('User account details are not available yet')
      return
    }

    if (!brandLogo) {
      warningToast('Please choose a logo to upload')
      return
    }

    setIsSavingBranding(true)
    try {
      await syncInvoiceProfileSettings()
      await refreshInvoiceProfile(userId)
      successToast('Branding settings saved successfully')
    } catch {
      errorToast('Unable to save branding settings')
    } finally {
      setIsSavingBranding(false)
    }
  }

  const removeBrandLogo = async () => {
    const userId = fetchedUserAccountData?.id
    if (!userId) return

    setIsRemovingLogo(true)
    try {
      await syncInvoiceProfileSettings(undefined, '')
      setBrandLogo('')
      setBrandLogoFileName('')
      await refreshInvoiceProfile(userId)
      successToast('Logo removed successfully')
    } catch {
      errorToast('Unable to remove logo')
    } finally {
      setIsRemovingLogo(false)
    }
  }

  const saveWarehouseSettings = async () => {
    if (!primaryWarehouse.trim()) {
      warningToast('Warehouse name is required')
      return
    }

    setIsSavingWarehouse(true)
    const payload = {
      api_name: primaryWarehouse,
      fields_data: [],
      address: {
        address_1: warehouseAddress1,
        address_2: warehouseAddress2,
        city: warehouseCity,
        state: selectedWarehouseState?.label || warehouseState,
        zip_code: '',
        country: selectedWarehouseCountry?.label || warehouseCountry,
        phone: warehouseContact,
        fax_number: '',
      },
      ...(warehouseId ? { isActive: true } : {}),
    }

    try {
      if (warehouseId) {
        await dispatch(updateWarehouseDetailsAsync({ id: warehouseId, payloadupdate: payload }) as any).unwrap()
      } else {
        await dispatch(createWarehouseDataAsync(payload) as any).unwrap()
      }

      successToast('Warehouse settings saved successfully')
      await loadPrimaryWarehouse()
    } catch {
      errorToast('Unable to save warehouse settings')
    } finally {
      setIsSavingWarehouse(false)
    }
  }

  const buildInvoiceProfilePayload = (templateOverride?: InvoiceTemplateRecord, logoOverride?: string) => {
    const activeTemplate =
      templateOverride ||
      invoiceTemplates.find((template) => template.isDefault) ||
      invoiceTemplates[0] ||
      createEmptyInvoiceTemplate()
    const existingTemplateId =
      activeTemplate.templateId ??
      (!String(activeTemplate.id).startsWith('new-template-') ? activeTemplate.id : null)

    return {
      id: fetchedUserAccountData?.id,
      type: 'INVOICE',
      platform_ids: activeTemplate.channels.map(Number).filter(Boolean),
      name: activeTemplate.name,
      company_name: companyName,
      sales_order_number_prefix: invoicePrefix,
      sales_order_number: Number(invoiceStartingNumber || 0),
      is_default: Boolean(activeTemplate.isDefault),
      ...(existingTemplateId ? { template_id: existingTemplateId } : {}),
      template_data: activeTemplate.templateHtml || '',
      financial_year: 'Apr-Mar',
      date_format: 'MM-dd-yyyy',
      street1: companyStreet1,
      street2: companyStreet2,
      zip: companyZip,
      city: companyCity,
      state: companyStateId,
      country: companyCountryId,
      organization_country: companyCountryId,
      orgnization_country: companyCountryId,
      brand_logo: logoOverride !== undefined ? logoOverride : brandLogo,
      include_vat: Boolean(gstNumber.trim()),
      vat_id: gstNumber,
      tax_number: taxId,
      tax_id: taxId,
      include_warranty: Boolean(activeTemplate.includeWarrantyDetails),
      include_imei: Boolean(activeTemplate.includeImei),
      include_serial: false,
      language_id: activeTemplate.language || 'en',
      notes: activeTemplate.notes,
      address: buildCompanyAddressPayload(),
      payment: {
        bic: '',
        iban: '',
        bank_detail: activeTemplate.bankDetails,
      },
      logo: logoOverride !== undefined ? logoOverride : brandLogo,
      currency_code: selectedCurrencyCode,
    }
  }

  const syncInvoiceProfileSettings = async (
    templateOverride?: InvoiceTemplateRecord,
    logoOverride?: string
  ) => {
    if (!fetchedUserAccountData?.id) {
      throw new Error('User account details are not available yet')
    }

    await dispatch(
      saveUserInvoiceProfileAsync({
        userId: fetchedUserAccountData.id,
        data: buildInvoiceProfilePayload(templateOverride, logoOverride),
      }) as any
    ).unwrap()
  }

  const buildPackingSlipProfilePayload = (
    templateOverride: PackingSlipTemplateRecord
  ) =>
    buildPackingSlipProfilePayloadFromRecord(templateOverride, {
      userAccountId: fetchedUserAccountData?.id,
      brandLogo,
    })

  const syncPackingSlipProfileSettings = async (
    templateOverride: PackingSlipTemplateRecord
  ) => {
    if (!fetchedUserAccountData?.id) {
      throw new Error('User account details are not available yet')
    }

    const response = await dispatch(
      saveUserInvoiceProfileAsync({
        userId: fetchedUserAccountData.id,
        data: buildPackingSlipProfilePayload(templateOverride),
      }) as any
    ).unwrap()

    return response
  }

  const savePackingSlipTemplate = async () => {
    if (!editingPackingSlipTemplate) {
      return
    }

    const templateName = editingPackingSlipTemplate.name.trim()

    if (!templateName) {
      warningToast('Template name is required')
      return
    }

    if (!editingPackingSlipTemplate.channels.length) {
      warningToast('Select at least one channel')
      return
    }

    const isDuplicateName = packingSlipTemplates.some(
      (template) =>
        template.name.trim().toLowerCase() === templateName.toLowerCase() &&
        template.id !== editingPackingSlipTemplate.id &&
        (
          !template.templateId ||
          !editingPackingSlipTemplate.templateId ||
          template.templateId !== editingPackingSlipTemplate.templateId
        )
    )

    if (isDuplicateName) {
      warningToast('Template name must be unique')
      return
    }

    if (editingPackingSlipTemplate.templateKind === 'custom') {
      if (
        isPackingSlipLegacyStandardTemplateData(editingPackingSlipTemplate.templateHtml)
      ) {
        warningToast(
          'Custom HTML templates cannot use standard layout JSON. Switch template type or replace the editor content with HTML before saving.'
        )
        return
      }

      const shortcodeBlockMessage = packingSlipShortcodeSaveBlockMessage(
        Boolean(editingPackingSlipTemplate.blockSavingUnknownSourceCode ?? true),
        customPackingSlipShortcodeValidation
      )

      if (shortcodeBlockMessage) {
        warningToast(shortcodeBlockMessage)
        return
      }
    }

    const normalizedTemplate = {
      ...editingPackingSlipTemplate,
      name: templateName,
    }

    setIsSavingPackingSlipTemplate(true)

    try {
      const response = await syncPackingSlipProfileSettings(normalizedTemplate)

      if (normalizedTemplate.isDefault) {
        const otherDefaultTemplates = packingSlipTemplates.filter(
          (template) =>
            template.isDefault &&
            template.templateId &&
            template.templateId !== normalizedTemplate.templateId
        )

        await Promise.all(
          otherDefaultTemplates.map((template) =>
            syncPackingSlipProfileSettings({
              ...template,
              isDefault: false,
            })
          )
        )
      }

      const userId = fetchedUserAccountData?.id

      if (!userId) {
        throw new Error('Unable to refresh packing slip templates')
      }

      const listResponse = await dispatch(
        getUserEmailTemplatesAsync({
          userId,
          type: 'PACKING_SLIP',
        }) as any
      )

      if (getUserEmailTemplatesAsync.fulfilled.match(listResponse)) {
        setPackingSlipTemplates(
          resolvePackingSlipTemplatesData(listResponse.payload)
        )
      } else {
        throw listResponse
      }

      successToast('Packing slip template saved successfully')
      closePackingSlipTemplateEditor()
    }catch (error: any) {
      errorToast(
        getApiErrorMessage(error, 'Unable to save packing slip template')
      )
    } finally {
      setIsSavingPackingSlipTemplate(false)
    }
  }

  const handleMfaToggle = () => {
    if (mfaEnabled) {
      setShowDisableMfaModal(true)
    } else {
      setShowMfaSetupModal(true)
      dispatch(generateMfaQrCodeAsync() as any)
    }
  }

  const verifyMfaEnable = async (val: { mfaCode: string }) => {
    setIsUpdatingMfa(true)
    try {
      await dispatch(verifyMfaCodeAsync({ code: val.mfaCode as any, mfa_enabled: true }) as any).unwrap()
      setShowMfaSetupModal(false)
      await dispatch(fetchUserAccountDetailsAsync(undefined))
      successToast('2-Factor Authentication Enabled Successfully')
    } catch (err: any) {
      errorToast(err?.data?.massage || 'Invalid authentication code')
    } finally {
      setIsUpdatingMfa(false)
    }
  }

  const verifyMfaDisable = async (val: { disablecode: string }) => {
    setIsUpdatingMfa(true)
    try {
      await dispatch(verifyMfaCodeAsync({ code: val.disablecode as any, mfa_enabled: false }) as any).unwrap()
      setShowDisableMfaModal(false)
      await dispatch(fetchUserAccountDetailsAsync(undefined))
      successToast('2-Factor Authentication Disabled Successfully')
    } catch (err: any) {
      errorToast(err?.data?.massage || 'Invalid authentication code')
    } finally {
      setIsUpdatingMfa(false)
    }
  }

  //   const handleManageCookiesVisibilityChange = async (event: ChangeEvent<HTMLInputElement>) => {
  //     if (isLoginAdminAccess) return
  //     if (!cookieConsentHydrated) return

  //     const nextConsent = {
  //       ...cookieConsent,
  //       banner_hidden: !event.target.checked,
  //     }

  //     const response = await dispatch(saveCookieConsentAsync(nextConsent))

  //     if (saveCookieConsentAsync.fulfilled.match(response)) {
  //       clearCookieConsent()
  //     } else {
  //       errorToast('Unable to update Manage Cookies visibility right now')
  //     }
  //   }

  const deletePackingSlipTemplate = (
    template: PackingSlipTemplateRecord
  ) => {
    if (!template.templateId) {
      warningToast('Unable to delete this packing slip template')
      return
    }

    setPackingSlipTemplateToDelete(template)
    setShowDeletePackingSlipModal(true)
  }

  const confirmDeletePackingSlipTemplate = async () => {
    if (
      !packingSlipTemplateToDelete?.templateId ||
      isDeletingPackingSlipTemplate
    ) {
      return
    }

    const template = packingSlipTemplateToDelete

    setIsDeletingPackingSlipTemplate(true)

    try {
      const response = await dispatch(
        deleteUserEmailTemplateAsync(Number(template.templateId)) as any
      )

      if (!deleteUserEmailTemplateAsync.fulfilled.match(response)) {
        throw response
      }

      setPackingSlipTemplates((prev) =>
        prev.filter(
          (item) =>
            item.templateId !== template.templateId &&
            item.id !== template.id
        )
      )

      if (
        editingPackingSlipTemplate?.templateId === template.templateId ||
        editingPackingSlipTemplate?.id === template.id
      ) {
        closePackingSlipTemplateEditor()
      }

      successToast('Packing slip template deleted successfully')
    } catch (error: any) {
      errorToast(
        getApiErrorMessage(error, 'Unable to delete packing slip template')
      )
    } finally {
      setIsDeletingPackingSlipTemplate(false)
      setShowDeletePackingSlipModal(false)
      setPackingSlipTemplateToDelete(null)
    }
  }

  const cancelDeletePackingSlipTemplate = () => {
    setShowDeletePackingSlipModal(false)
    setPackingSlipTemplateToDelete(null)
  }

  const openNewInvoiceTemplate = () => {
    setEditingInvoiceTemplate(createEmptyInvoiceTemplate())
  }

  const openNewPackingSlipTemplate = () => {
    setEditingPackingSlipTemplate(createEmptyPackingSlipTemplate())
  }

  const openExistingPackingSlipTemplate = (templateId: string) => {
    const matchedTemplate = packingSlipTemplates.find(
      (template) => template.id === templateId
    )

    if (matchedTemplate) {
      setEditingPackingSlipTemplate({
        ...matchedTemplate,
        templateKind: matchedTemplate.templateKind ?? 'standard',
      })
    }
  }


  const updatePackingSlipTemplate = (
  field: keyof PackingSlipTemplateRecord,
  value: any
) => {
  setEditingPackingSlipTemplate((prev) =>
    prev ? { ...prev, [field]: value } : prev
  )
}

  const handlePackingSlipTemplateKindChange = (nextKind: PackingSlipTemplateKind) => {
    if (!editingPackingSlipTemplate) {
      return
    }

    const plan = planPackingSlipTemplateKindSwitch(editingPackingSlipTemplate, nextKind)
    if (!plan) {
      return
    }

    if (plan.requiresConfirmation) {
      setPackingSlipKindSwitchPrompt(plan)
      return
    }

    setEditingPackingSlipTemplate({
      ...editingPackingSlipTemplate,
      ...plan.patch,
    })
  }

  const confirmPackingSlipKindSwitch = () => {
    if (!editingPackingSlipTemplate || !packingSlipKindSwitchPrompt) {
      return
    }

    setEditingPackingSlipTemplate({
      ...editingPackingSlipTemplate,
      ...packingSlipKindSwitchPrompt.patch,
    })
    setPackingSlipKindSwitchPrompt(null)
  }

  const cancelPackingSlipKindSwitch = () => {
    setPackingSlipKindSwitchPrompt(null)
  }

  const closePackingSlipTemplateEditor = () => {
    setPackingSlipKindSwitchPrompt(null)
    setEditingPackingSlipTemplate(null)
  }

  const openExistingInvoiceTemplate = (templateId: string) => {
    const matchedTemplate = invoiceTemplates.find((template) => template.id === templateId)
    if (matchedTemplate) {
      setEditingInvoiceTemplate({ ...matchedTemplate })
    }
  }

  const updateEditingInvoiceTemplate = (field: keyof InvoiceTemplateRecord, value: any) => {
    setEditingInvoiceTemplate((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  const updateInvoiceChannels = (selectedOptions: MultiValue<InvoiceChannelOption>) => {
    const channels = selectedOptions.map((option) => option.value)

    setEditingInvoiceTemplate((prev) =>
      prev
        ? {
          ...prev,
          channels,
          channelLabel: channels[0] || '',
        }
        : prev
    )
  }

  const saveInvoiceTemplate = () => {
    if (!editingInvoiceTemplate) {
      return
    }

    const templateName = editingInvoiceTemplate.name.trim()

    if (!templateName) {
      warningToast('Template name is required')
      return
    }

    const isDuplicateName = invoiceTemplates.some(
      (template) =>
        template.name.trim().toLowerCase() === templateName.toLowerCase() &&
        template.id !== editingInvoiceTemplate.id &&
        (!template.templateId || !editingInvoiceTemplate.templateId || template.templateId !== editingInvoiceTemplate.templateId)
    )

    if (isDuplicateName) {
      warningToast('Template name must be unique')
      return
    }

    if (!editingInvoiceTemplate.channels.length) {
      warningToast('Select at least one channel')
      return
    }

    const firstPlatformId = editingInvoiceTemplate.channels[0]
    const firstPlatform = apiSecondaryPlatforms.find((p) => String(p.id) === firstPlatformId)
    const normalizedTemplate = {
      ...editingInvoiceTemplate,
      name: templateName,
      channelLabel: firstPlatform?.display_name || firstPlatform?.name || firstPlatformId || '',
    }

    setIsSavingInvoiceTemplate(true)
    syncInvoiceProfileSettings(normalizedTemplate)
      .then(async () => {
        let refreshedTemplates: InvoiceTemplateRecord[] = []
        if (fetchedUserAccountData?.id) {
          const refreshedProfile = await refreshInvoiceProfile(fetchedUserAccountData.id)
          refreshedTemplates = resolveInvoiceTemplatesData(refreshedProfile, resolveInvoiceProfileData(refreshedProfile))
        }
        setInvoiceTemplates((prev) => {
          const savedTemplate =
            refreshedTemplates.find((template) => template.templateId === normalizedTemplate.templateId) ||
            refreshedTemplates.find((template) => template.name === normalizedTemplate.name)
          const nextTemplate = savedTemplate || normalizedTemplate
          const hasSavedTemplate = prev.some(
            (template) =>
              template.id === nextTemplate.id ||
              (template.templateId && nextTemplate.templateId && template.templateId === nextTemplate.templateId)
          )
          if (hasSavedTemplate || !String(normalizedTemplate.id).startsWith('new-template-')) {
            return prev.map((template) =>
              template.id === normalizedTemplate.id ||
                (template.templateId && nextTemplate.templateId && template.templateId === nextTemplate.templateId)
                ? { ...template, ...nextTemplate }
                : template
            )
          }

          return [nextTemplate, ...prev]
        })
        successToast('Invoice template saved successfully')
        setEditingInvoiceTemplate(null)
      })
      .catch((error: any) => {
        errorToast(
          getApiErrorMessage(error, 'Unable to save invoice template')
        )
      })
      .finally(() => setIsSavingInvoiceTemplate(false))
  }

  const saveInvoiceSeries = () => {
    if (!invoicePrefix.trim()) {
      warningToast('Invoice prefix is required')
      return
    }

    if (!invoiceStartingNumber.trim()) {
      warningToast('Starting number is required')
      return
    }

    setIsSavingInvoiceSeries(true)
    syncInvoiceProfileSettings()
      .then(() => {
        successToast('Invoice series saved successfully')
      })
      .catch(() => {
        errorToast('Unable to save invoice series')
      })
      .finally(() => setIsSavingInvoiceSeries(false))
  }

  const renderInvoiceTemplatePreview = (template: InvoiceTemplateRecord, compact = false) => {
    const sellerAddress = [companyStreet1, companyStreet2, companyCity, selectedCompanyState?.label, selectedCompanyCountry?.label]
      .filter(Boolean)
      .join(', ')
    const totalAmount = template.lineItems[0]?.amount || '$0.00'

    return (
      <div
        className={
          compact
            ? 'account-settings-modern__invoice-preview account-settings-modern__invoice-preview--compact'
            : 'account-settings-modern__invoice-preview'
        }
      >
        <div className='account-settings-modern__invoice-header'>
          <div>
            {template.logoUrl || brandLogo ? (
              <img
                className='account-settings-modern__invoice-logo-img'
                src={template.logoUrl || brandLogo}
                alt='Invoice logo'
              />
            ) : (
              <div className='account-settings-modern__brand-name'>{companyName || template.name || 'Invoice Template'}</div>
            )}
            <div className='account-settings-modern__seller-text'>
              <strong>{companyName || 'Company Name'}</strong>
              <br />
              {sellerAddress || 'Company address'}
              <br />
              VAT ID: {template.vatNumber || gstNumber || '-'}
              <br />
              Tax No: {template.taxNumber || taxId || '-'}
              <br />
              {email || 'company@email.com'}
            </div>
          </div>
          <div className='account-settings-modern__invoice-id'>
            <strong>INVOICE</strong>
            <span># {template.invoiceNumber}</span>
          </div>
        </div>

        <div className='account-settings-modern__invoice-blocks'>
          <div>
            <small>Bill To</small>
            <p>
              {template.billToName || 'Customer Name'}
              <br />
              {template.billToCompany || template.channelLabel}
              <br />
              {template.billToAddress || 'Customer address'}
            </p>
          </div>
          <div>
            <small>Details</small>
            <p>
              Invoice Date: {template.invoiceDate}
              <br />
              Channel: {template.channelLabel}
              <br />
              Language: {template.language}
            </p>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>SKU</th>
              <th>Item & Description</th>
              <th>Qty</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {template.lineItems.map((item, index) => (
              <tr key={`${template.id}-${item.sku}-${index}`}>
                <td>{index + 1}</td>
                <td>{item.sku}</td>
                <td>
                  {item.description}
                  {template.includeImei && item.imei ? (
                    <span className='account-settings-modern__imei'>IMEI: {item.imei}</span>
                  ) : null}
                </td>
                <td>{item.quantity}</td>
                <td>{item.amount}</td>
              </tr>
            ))}
            {!template.lineItems.length ? (
              <tr>
                <td colSpan={5}>No items</td>
              </tr>
            ) : null}
          </tbody>
        </table>

        <div className='account-settings-modern__invoice-total'>
          <span>Total</span>
          <strong>{totalAmount}</strong>
        </div>

        <div className='account-settings-modern__invoice-notes'>
          <div className='account-settings-modern__invoice-notes-line'>
            <strong>Notes:</strong>
            <div
              className='account-settings-modern__invoice-notes-content'
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.notes || '') }}
            ></div>
          </div>
          {template.bankDetails ? (
            <div className='account-settings-modern__invoice-notes-line'>
              <strong>Bank:</strong>
              <div
                className='account-settings-modern__invoice-notes-content'
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.bankDetails || '') }}
              ></div>
            </div>
          ) : null}
          {template.signatureUrl ? <span>Signature: {template.signatureUrl}</span> : null}
        </div>
      </div>
    )
  }

  const renderPackingSlipPreview = (template: PackingSlipTemplateRecord) => {
    const pageFormat =
      PACKING_SLIP_PAGE_FORMATS[
        template.paperSize as keyof typeof PACKING_SLIP_PAGE_FORMATS
      ] ?? PACKING_SLIP_PAGE_FORMATS['4x6']

    return (
      <div
        className='packing-slip-preview'
        style={{
          width: pageFormat.width,
          height: pageFormat.height,
        }}
      >
        <div className='packing-slip-preview__header'>
          {template.displayBrandLogo ? (
            template.logoUrl || brandLogo ? (
              <img
                src={template.logoUrl || brandLogo}
                alt='Brand Logo'
                className='packing-slip-preview__logo'
              />
            ) : (
              <div className='packing-slip-preview__logo-placeholder'>
                BRAND LOGO
              </div>
            )
          ) : null}

          <strong>PACKING SLIP</strong>
        </div>

        <div className='packing-slip-preview__meta'>
          <div>
            <strong>*ORD-88912*</strong>
          </div>

          <div>
            <div>Ref: Shopify #10492</div>
            <div>Date: Aug 06, 2026</div>

            {template.displayShippingMethod ? (
              <div>Ship: FedEx Express Saver</div>
            ) : null}
          </div>
        </div>

        <div className='packing-slip-preview__addresses'>
          <div>
            <strong>SHIP TO</strong>
            <p>
              <strong>Sarah Jenkins</strong>
              <br />
              742 Evergreen Terrace
              <br />
              Springfield, OR 97477
            </p>
          </div>

          <div>
            <strong>SHIP FROM</strong>
            <p>
              <strong>Acme Logistics</strong>
              <br />
              Bay 12, Industrial Pkwy
              <br />
              Columbus, OH 43215
            </p>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              {template.displayProductImages ? <th>IMG</th> : null}
              <th>ITEM & SKU</th>
              {template.displayWarehouseBins ? <th>BIN</th> : null}
              <th>QTY</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              {template.displayProductImages ? (
                <td>
                  <div className='packing-slip-preview__product-image'>
                    Bag
                  </div>
                </td>
              ) : null}

              <td>
                <strong>Hiking Backpack 30L</strong>
                <br />
                <small>HK-BP-30L-BLU</small>
              </td>

              {template.displayWarehouseBins ? <td>B-12</td> : null}

              <td>1</td>
            </tr>

            <tr>
              {template.displayProductImages ? (
                <td>
                  <div className='packing-slip-preview__product-image'>
                    Bottle
                  </div>
                </td>
              ) : null}

              <td>
                <strong>Steel Water Bottle</strong>
                <br />
                <small>WB-SST-750-BLK</small>
              </td>

              {template.displayWarehouseBins ? <td>A-05</td> : null}

              <td>2</td>
            </tr>
          </tbody>
        </table>

        {template.displayCustomerNotes ? (
          <div className='packing-slip-preview__customer-note'>
            <strong>CUSTOMER NOTE:</strong>
            <div className=''>
              Please leave package behind planter box on front porch.
            </div>
          </div>
        ) : null}

        <div className='packing-slip-preview__footer'>
          Order ID: <strong>#ORD-88912</strong> | Items: 3 | Page: <strong>1 of 1</strong>
        </div>
      </div>
    )
  }

  const renderPackingSlipTemplateGrid = () => (
    <div className='account-settings-modern__content-shell account-settings-modern__full'>
      <div className='account-settings-modern__header-row'>
        <h2 className='account-settings-modern__title'>
          All Packing Slip Templates
        </h2>

        <button
          className='account-settings-modern__btn-primary'
          onClick={openNewPackingSlipTemplate}
        >
          + New Template
        </button>
      </div>

      <div className='account-settings-modern__template-grid'>
        {packingSlipTemplates.map((template) => (
          <div
            className='account-settings-modern__template-card'
            key={template.id}
          >
            <div className='account-settings-modern__template-preview'>
              {template.isDefault ? (
                <span className='account-settings-modern__badge-default'>
                  Default
                </span>
              ) : null}

              {renderPackingSlipPreview(template)}
            </div>

            <div className='account-settings-modern__template-footer'>
              <span>{template.name}</span>

              <div className='packing-slip-template-actions d-flex gap-3 align-items-center'>
                <button
                  type='button'
                  className='btn btn-icon btn-bg-light btn-color-primary btn-sm'
                  title='Edit'
                  style={{ cursor: 'pointer' }}
                  onClick={() => openExistingPackingSlipTemplate(template.id)}
                >
                  <KTIcon iconName='pencil' className='fs-4 text-primary' />
                </button>

                <button
                  type='button'
                  className='btn btn-icon btn-bg-light btn-color-danger btn-sm'
                  title='Delete'
                  style={{ cursor: 'pointer' }}
                  onClick={() => deletePackingSlipTemplate(template)}
                >
                  <KTIcon iconName='trash' className='fs-4 text-danger' />
                </button>
              </div>
            </div>
          </div>
        ))}

        <CommonAlertModal
          title='Delete Packing Slip Template'
          subtitle={
            packingSlipTemplateToDelete
              ? `Are you sure you want to delete "${packingSlipTemplateToDelete.name}"?`
              : 'Are you sure you want to delete this packing slip template?'
          }
          isVisible={showDeletePackingSlipModal}
          onSuccess={confirmDeletePackingSlipTemplate}
          onCancel={cancelDeletePackingSlipTemplate}
          cancelBtnTitle='No'
          successBtnTitle={
            isDeletingPackingSlipTemplate ? 'Deleting...' : 'Yes, Delete'
          }
          successBtnClasses='btn btn-sm btn-danger m-2'
          successDisabled={isDeletingPackingSlipTemplate}
          showSaveButton={true}
          showCloseButton={!isDeletingPackingSlipTemplate}
          isTermsVisible={false}
        />


        <button
          className='account-settings-modern__template-card account-settings-modern__template-add'
          onClick={openNewPackingSlipTemplate}
        >
          <span>+</span>
          <p>Add New Template</p>
        </button>
      </div>

      {!packingSlipTemplates.length ? (
        <p className='account-settings-modern__empty-state'>
          No packing slip templates found.
        </p>
      ) : null}
    </div>
  )

  const renderTemplateGrid = () => (
    <div className='account-settings-modern__content-shell account-settings-modern__full'>
      <div className='account-settings-modern__header-row'>
        <h2 className='account-settings-modern__title'>All Invoice Templates</h2>
        <button className='account-settings-modern__btn-primary' onClick={openNewInvoiceTemplate}>
          + New Template
        </button>
      </div>

      <div className='account-settings-modern__template-grid'>
        {invoiceTemplates.map((template) => (
          <div className='account-settings-modern__template-card' key={template.id}>
            <div className='account-settings-modern__template-preview'>
              {template.isDefault ? <span className='account-settings-modern__badge-default'>Default</span> : null}
              {renderInvoiceTemplatePreview(template, true)}
            </div>
            <div className='account-settings-modern__template-footer'>
              <span>{template.name}</span>
              <button className='account-settings-modern__btn-link' onClick={() => openExistingInvoiceTemplate(template.id)}>
                Edit
              </button>
            </div>
          </div>
        ))}

        <button
          className='account-settings-modern__template-card account-settings-modern__template-add'
          onClick={openNewInvoiceTemplate}
        >
          <span>+</span>
          <p>Add New Template</p>
        </button>
      </div>
      {!invoiceTemplates.length ? (
        <p className='account-settings-modern__empty-state'>No invoice templates found.</p>
      ) : null}
    </div>
  )

  const renderContent = () => {
    switch (activeSection) {
      case 'my-account-profile':
        return (
          <div className='account-settings-modern__content-shell account-settings-modern__full'>
            <div className='account-settings-modern__card'>
              <div className='account-settings-modern__two-col-row'>
                <div className='account-settings-modern__form-group'>
                  <label>Full Name</label>
                  <input type='text' value={fullName} onChange={(e) => setFullName(e.target.value)} />
                </div>
                <div className='account-settings-modern__form-group'>
                  <label>Email Address</label>
                  <p className='account-settings-modern__static-value'>{email}</p>
                  <p className='account-settings-modern__helper-text'>
                    For security reasons, this cannot be changed directly.{' '}
                    <a href='mailto:support@mycommercium.com' className='account-settings-modern__link-text'>Request Change</a>
                  </p>
                </div>
              </div>
              <div className='account-settings-modern__form-group'>
                <label>Phone Number</label>
                <div className='account-settings-modern__phone-row'>
                  <div className='account-settings-modern__flag-dropdown'>
                    <button
                      type='button'
                      className='account-settings-modern__flag-trigger'
                      onClick={() => {
                        setIsCountryDropdownOpen((prev) => !prev)
                        setCountrySearchQuery('')
                      }}
                    >
                      {selectedCountry && (
                        <img
                          src={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png`}
                          alt={selectedCountry.code}
                          width='20'
                          height='14'
                          style={{ borderRadius: '2px', marginRight: '6px' }}
                        />
                      )}
                      <span>{selectedCountry?.dial_code || '+91'}</span>
                      <span style={{ marginLeft: '4px', fontSize: '0.7rem' }}>▾</span>
                    </button>
                    {isCountryDropdownOpen && (
                      <div className='account-settings-modern__flag-menu'>
                        <input
                          type='text'
                          className='account-settings-modern__flag-search'
                          placeholder='Search country...'
                          value={countrySearchQuery}
                          onChange={(e) => setCountrySearchQuery(e.target.value)}
                          autoFocus
                        />
                        <div className='account-settings-modern__flag-list'>
                          {countries
                            .filter((c) =>
                              `${c.name} ${c.dial_code}`.toLowerCase().includes(countrySearchQuery.toLowerCase())
                            )
                            .map((country) => (
                              <button
                                key={country.code}
                                type='button'
                                className='account-settings-modern__flag-option'
                                onClick={() => {
                                  setSelectedCountry(country)
                                  setIsCountryDropdownOpen(false)
                                  setCountrySearchQuery('')
                                }}
                              >
                                <img
                                  src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                                  alt={country.code}
                                  width='20'
                                  height='14'
                                  style={{ borderRadius: '2px', marginRight: '8px' }}
                                />
                                {country.dial_code} {country.name}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    className='account-settings-modern__phone-input'
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder='Enter phone number'
                  />
                </div>
                <p className='account-settings-modern__helper-text'>
                  Country code comes from the selector. Enter digits only in the number field.
                </p>
              </div>
              <button className='account-settings-modern__btn-primary' onClick={saveProfileDetails}>
                {isSavingProfile ? 'Saving...' : 'Save Profile Changes'}
              </button>
            </div>
          </div>
        )

      case 'my-account-security':
        return (
          <div className='account-settings-modern__content-shell account-settings-modern__full account-settings-modern__section-content-width'>
            <p className='account-settings-modern__section-description'>
              Manage your password and protect your account with additional authentication.
            </p>

            <div className='account-settings-modern__card'>
              <span className='account-settings-modern__card-label'>Change Password</span>
              <div className='account-settings-modern__form-group account-settings-modern__pw-group'>
                <label>Current Password</label>
                <div className='account-settings-modern__pw-wrap' style={passwordErrors.previous ? { borderColor: '#dc3545' } : undefined}>
                  <input
                    type={showPrevPassword ? 'text' : 'password'}
                    placeholder='Enter current password'
                    value={previousPassword}
                    onChange={(e) => { setPreviousPassword(e.target.value); setPasswordErrors((p) => ({ ...p, previous: undefined })) }}
                  />
                  <button type='button' className='account-settings-modern__pw-eye' onClick={() => setShowPrevPassword((v) => !v)}>
                    <FontAwesomeIcon icon={showPrevPassword ? faEye : faEyeSlash} />
                  </button>
                </div>
                {passwordErrors.previous && <span className='account-settings-modern__field-error'>{passwordErrors.previous}</span>}
              </div>
              <div className='account-settings-modern__form-group account-settings-modern__pw-group'>
                <label>New Password</label>
                <div className='account-settings-modern__pw-wrap' style={passwordErrors.new ? { borderColor: '#dc3545' } : undefined}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder='Enter new password'
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setPasswordErrors((p) => ({ ...p, new: undefined })) }}
                  />
                  <button type='button' className='account-settings-modern__pw-eye' onClick={() => setShowNewPassword((v) => !v)}>
                    <FontAwesomeIcon icon={showNewPassword ? faEye : faEyeSlash} />
                  </button>
                </div>
                {passwordErrors.new && <span className='account-settings-modern__field-error'>{passwordErrors.new}</span>}
              </div>
              <div className='account-settings-modern__form-group account-settings-modern__pw-group'>
                <label>Confirm New Password</label>
                <div className='account-settings-modern__pw-wrap' style={passwordErrors.confirm ? { borderColor: '#dc3545' } : undefined}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder='Confirm new password'
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setPasswordErrors((p) => ({ ...p, confirm: undefined })) }}
                  />
                  <button type='button' className='account-settings-modern__pw-eye' onClick={() => setShowConfirmPassword((v) => !v)}>
                    <FontAwesomeIcon icon={showConfirmPassword ? faEye : faEyeSlash} />
                  </button>
                </div>
                {passwordErrors.confirm && <span className='account-settings-modern__field-error'>{passwordErrors.confirm}</span>}
              </div>
              <button className='account-settings-modern__btn-primary' onClick={savePassword}>
                {isSavingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </div>

            <div className='account-settings-modern__card'>
              <div className='account-settings-modern__toggle-row'>
                <div>
                  <span className='account-settings-modern__card-label account-settings-modern__mb-6'>
                    Two-Factor Authentication (2FA)
                    {!mfaEnabled && (
                      <span className='account-settings-modern__badge-disabled'>Disabled</span>
                    )}
                  </span>
                  <p className='account-settings-modern__muted'>
                    Require a verification code for an extra layer of security.
                  </p>
                </div>
                <label className='account-settings-modern__switch'>
                  <input type='checkbox' checked={mfaEnabled} onChange={handleMfaToggle} disabled={isUpdatingMfa} />
                  <span className='account-settings-modern__slider'></span>
                </label>
              </div>
            </div>

            {/* {!isLoginAdminAccess && (
              <div className='account-settings-modern__card'>
                <div className='account-settings-modern__toggle-row'>
                  <div>
                    <span className='account-settings-modern__card-label account-settings-modern__mb-6'>
                      Show Manage Cookies
                    </span>
                    <p className='account-settings-modern__muted'>
                      Control whether the floating Manage Cookies pill remains visible.
                    </p>
                  </div>
                  <label className='account-settings-modern__switch'>
                    <input
                      type='checkbox'
                      checked={cookieConsentHydrated ? !cookieConsent.banner_hidden : false}
                      disabled={
                        !cookieConsentHydrated ||
                        cookieConsentSaving ||
                        fetchedUserAccountData?.role?.customer_access === 'READ' ||
                        fetchedUserAccountData?.role?.customer_access === 'RESTRICT'
                      }
                      onChange={handleManageCookiesVisibilityChange}
                    />
                    <span className='account-settings-modern__slider'></span>
                  </label>
                </div>
              </div>
            )} */}
          </div>
        )

      case 'my-account-notifications':
        return (
          <div className='account-settings-modern__content-shell account-settings-modern__full account-settings-modern__section-content-width account-settings-modern__notifications'>
            <p className='account-settings-modern__section-description'>
              Manage alert recipients and choose which updates to receive.
            </p>

            <div className='account-settings-modern__card'>
              <span className='account-settings-modern__card-label'>Additional Notification Recipients</span>
              <p className='account-settings-modern__muted account-settings-modern__mb-14'>
                Enter email addresses separated by commas to include more staff in notifications.
              </p>
              <textarea
                rows={3}
                placeholder='email1@example.com, email2@example.com'
                value={notifications.staff_list}
                onChange={(e) => setNotifications((prev) => ({ ...prev, staff_list: e.target.value }))}
                disabled={isSavingNotifications}
              ></textarea>

              <button
                type='button'
                className='account-settings-modern__btn-primary account-settings-modern__mt-16'
                onClick={saveNotificationSettings}
                disabled={isSavingNotifications}
              >
                {isSavingNotifications ? 'Saving...' : 'Update Recipients'}
              </button>
            </div>

            <div className='account-settings-modern__card account-settings-modern__notify-prefs-card'>
              <div className='account-settings-modern__notify-prefs-body'>
                <div className='account-settings-modern__notify-prefs-header'>
                  <div>
                    <h3 className='account-settings-modern__notify-prefs-title'>Notification Preferences</h3>
                    <p className='account-settings-modern__muted account-settings-modern__notify-prefs-subtitle'>
                      Choose which types of updates are emailed. All are on by default.
                    </p>
                  </div>
                  <button
                    type='button'
                    className='account-settings-modern__btn-secondary account-settings-modern__notify-prefs-reset'
                    onClick={resetNotificationCategoriesToDefault}
                    disabled={isSavingNotifications}
                  >
                    Reset to default
                  </button>
                </div>

                <div className='account-settings-modern__notify-prefs-master'>
                  <div className='account-settings-modern__notify-prefs-master-text'>
                    <span className='account-settings-modern__notify-prefs-master-label'>All email notifications</span>
                    <span className='account-settings-modern__muted account-settings-modern__notify-prefs-master-meta'>
                      {enabledNotificationCategoryCount} of {NOTIFICATION_CATEGORY_KEYS.length} categories on
                    </span>
                  </div>
                  <div className='account-settings-modern__notify-prefs-master-actions'>
                    <span className='account-settings-modern__notify-prefs-status'>
                      {enabledNotificationCategoryCount > 0 ? 'On' : 'Off'}
                    </span>
                    <label className='account-settings-modern__switch'>
                      <input
                        type='checkbox'
                        checked={allNotificationCategoriesEnabled}
                        onChange={(e) => setMasterNotificationCategories(e.target.checked)}
                        disabled={isSavingNotifications}
                      />
                      <span className='account-settings-modern__slider'></span>
                    </label>
                  </div>
                </div>

                <div className='account-settings-modern__notify-prefs-rows'>
                  {NOTIFICATION_CATEGORY_ROWS.map((row) => (
                    <div className='account-settings-modern__notify-prefs-row' key={row.field}>
                      <div className='account-settings-modern__notify-prefs-row-icon' aria-hidden='true'>
                        <KTIcon iconName={row.iconName} iconType='outline' className='fs-2 text-primary' />
                      </div>
                      <div className='account-settings-modern__notify-prefs-row-body'>
                        <span className='account-settings-modern__notify-prefs-row-title'>{row.title}</span>
                        <span className='account-settings-modern__muted account-settings-modern__notify-prefs-row-desc'>
                          {row.description}
                        </span>
                      </div>
                      <div className='account-settings-modern__notify-prefs-row-control'>
                        <label className='account-settings-modern__switch account-settings-modern__notify-prefs-row-switch'>
                          <input
                            type='checkbox'
                            checked={notifications[row.field]}
                            onChange={(e) => setNotificationCategoryEnabled(row.field, e.target.checked)}
                            disabled={isSavingNotifications}
                          />
                          <span className='account-settings-modern__slider'></span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                <div className='account-settings-modern__notify-prefs-lock'>
                  <KTIcon iconName='lock' iconType='outline' className='fs-4 text-muted' />
                  <span className='account-settings-modern__muted'>
                    Security, billing and channel-disconnected alerts are always sent and can&apos;t be turned off.
                  </span>
                </div>
              </div>

              <div className='account-settings-modern__notify-prefs-footer'>
                <div className='account-settings-modern__notify-prefs-actions'>
                  <button
                    type='button'
                    className='account-settings-modern__btn-secondary'
                    onClick={cancelNotificationSettingsEdits}
                    disabled={isSavingNotifications}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    className='account-settings-modern__btn-primary'
                    onClick={saveNotificationSettings}
                    disabled={isSavingNotifications}
                  >
                    {isSavingNotifications ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )

      case 'my-account-application-logs':
        if (!canAccessApplicationLogs) {
          return null
        }

        return (
          <div className='account-settings-modern__content-shell account-settings-modern__full'>
            <p className='account-settings-modern__section-description'>
              Review account activity and configuration changes.
            </p>
            <NewHistoryLogs />
          </div>
        )

      case 'company-profile':
        return (
          <div className='account-settings-modern__content-shell account-settings-modern__full'>
            <p className='account-settings-modern__section-description'>
              Update your business identity and tax information used for invoices.
            </p>

            <div className='account-settings-modern__card'>
              <div className='account-settings-modern__form-group'>
                <label>Company Name*</label>
                <input type='text' value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
              </div>
              <div className='account-settings-modern__three-col-row'>
                <div className='account-settings-modern__form-group'>
                  <label>VAT / GST Number</label>
                  <input type='text' value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} />
                </div>
                <div className='account-settings-modern__form-group'>
                  <label>Tax ID (Only for EU Region)</label>
                  <input type='text' value={taxId} onChange={(e) => setTaxId(e.target.value)} />
                </div>
                <div className='account-settings-modern__form-group'>
                  <label>Currency</label>
                  <Select
                    classNamePrefix='account-settings-modern-select'
                    isLoading={isLoadingCurrencies}
                    options={currencyOptions}
                    value={currencyOptions.find(c => c.value === selectedCurrencyCode) || null}
                    placeholder='Select currency'
                    isClearable={true}
                    maxMenuHeight={200}
                    styles={{
                      option: (base) => ({
                        ...base,
                        display: 'flex',
                        alignItems: 'center',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                      }),
                      singleValue: (base) => ({
                        ...base,
                        display: 'flex',
                        alignItems: 'center',
                      })
                    }}
                    filterOption={(option: any, inputValue: string) => {
                      const search = inputValue.toLowerCase();
                      return (
                        option.data.value.toLowerCase().includes(search) ||
                        option.data.name.toLowerCase().includes(search)
                      );
                    }}
                    onChange={(option: any) => setSelectedCurrencyCode(option?.value || '')}
                  />
                </div>
              </div>
              <div className='account-settings-modern__four-col-row'>
                <div className='account-settings-modern__form-group'>
                  <label>Country*</label>
                  <Select
                    classNamePrefix='account-settings-modern-select'
                    isLoading={isLoadingCountries}
                    options={companyCountryOptions}
                    value={selectedCompanyCountry}
                    placeholder='Select country'
                    onChange={(option: any) => {
                      setCompanyCountryId(option?.value ?? null)
                      setCompanyStateId(null)
                    }}
                  />
                </div>
                <div className='account-settings-modern__form-group'>
                  <label>State</label>
                  <Select
                    classNamePrefix='account-settings-modern-select'
                    isDisabled={!companyCountryId}
                    isLoading={isLoadingStates}
                    options={companyStateOptions}
                    value={selectedCompanyState}
                    placeholder='Select state'
                    onChange={(option: any) => setCompanyStateId(option?.value ?? null)}
                  />
                </div>
                <div className='account-settings-modern__form-group'>
                  <label>Zip Code</label>
                  <input type='text' value={companyZip} onChange={(e) => setCompanyZip(e.target.value)} />
                </div>
                <div className='account-settings-modern__form-group'>
                  <label>City</label>
                  <input type='text' value={companyCity} onChange={(e) => setCompanyCity(e.target.value)} />
                </div>
              </div>
              <div className='account-settings-modern__two-col-row'>
                <div className='account-settings-modern__form-group'>
                  <label>Street Address 1</label>
                  <input type='text' value={companyStreet1} onChange={(e) => setCompanyStreet1(e.target.value)} />
                </div>
                <div className='account-settings-modern__form-group'>
                  <label>Street Address 2</label>
                  <input type='text' value={companyStreet2} onChange={(e) => setCompanyStreet2(e.target.value)} />
                </div>
              </div>
              <button className='account-settings-modern__btn-primary' onClick={saveCompanyDetails}>
                {isSavingCompany ? 'Saving...' : 'Save Company Details'}
              </button>
            </div>
          </div>
        )

      case 'company-branding':
        return (
          <div className='account-settings-modern__content-shell account-settings-modern__full'>
            <p className='account-settings-modern__section-description'>
              Customize how your brand appears on transaction PDFs and system emails.
            </p>

            <div className='account-settings-modern__card'>
              <label>Company Logo</label>
              <label className='account-settings-modern__upload-box' htmlFor='company-logo-upload'>
                <input
                  id='company-logo-upload'
                  type='file'
                  accept='image/png,image/jpeg,image/gif'
                  onChange={handleBrandLogoChange}
                  hidden
                />
                <span className='account-settings-modern__upload-plus'>+</span>
                <span className='account-settings-modern__upload-title'>Upload Your Organization Logo</span>
                <p className='account-settings-modern__upload-hint'>
                  This logo will be displayed on all invoices and reports. Preferred: 240 x 240 pixels.
                  Supported: JPG, PNG, GIF.
                </p>
              </label>
              {brandLogo ? (
                <div className='account-settings-modern__logo-preview'>
                  <img src={brandLogo} alt='Company logo preview' />
                  <div>
                    <span>{brandLogoFileName || 'Saved company logo'}</span>
                    <button
                      type='button'
                      className='account-settings-modern__btn-link'
                      disabled={isRemovingLogo || isSavingBranding}
                      onClick={removeBrandLogo}
                    >
                      {isRemovingLogo ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </div>
              ) : null}
              <button className='account-settings-modern__btn-primary' onClick={saveBrandingSettings} disabled={isSavingBranding}>
                {isSavingBranding ? 'Saving...' : 'Save Branding Settings'}
              </button>
            </div>
          </div>
        )

      case 'company-warehouses':
        return (
          <div className='account-settings-modern__embedded-section'>
            <WarehouseListing />
          </div>
        )

      case 'company-invoice-series':
        return (
          <div className='account-settings-modern__content-shell account-settings-modern__full'>
            <div className='account-settings-modern__series-card'>
              <div className='account-settings-modern__series-grid'>
                <div className='account-settings-modern__series-cell'>
                  <label>Prefix</label>
                  <input
                    type='text'
                    value={invoicePrefix}
                    onChange={(e) => setInvoicePrefix(e.target.value)}
                    placeholder='INV-'
                  />
                </div>
                <div className='account-settings-modern__series-cell'>
                  <label>
                    Starting Number <span title='This number is used for the next generated invoice.'>i</span>
                  </label>
                  <input
                    type='text'
                    inputMode='numeric'
                    value={invoiceStartingNumber}
                    onChange={(e) => setInvoiceStartingNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder='000001'
                  />
                </div>
                <div className='account-settings-modern__series-cell account-settings-modern__series-cell--preview'>
                  <label>
                    Preview <span title='Preview is generated from prefix and starting number.'>i</span>
                  </label>
                  <div className='account-settings-modern__series-preview'>{invoiceSeriesPreview}</div>
                </div>
              </div>
            </div>
            <div className='account-settings-modern__series-actions'>
              <button className='account-settings-modern__btn-primary' onClick={saveInvoiceSeries}>
                {isSavingInvoiceSeries ? 'Saving...' : 'Save Invoice Series'}
              </button>
            </div>
          </div>
        )

      case 'user-management-users':
        return (
          <div className='account-settings-modern__embedded-section'>
            <AddManageUserFunctionality />
          </div>
        )

      case 'user-management-roles':
        return (
          <div className='account-settings-modern__embedded-section'>
            <ManageRolesFunctionality />
          </div>
        )

      case 'template-invoice':
        if (!editingInvoiceTemplate) {
          return renderTemplateGrid()
        }

        return (
          <div className='account-settings-modern__editor-layout'>
            <div className='account-settings-modern__editor-pane'>
              <div className='account-settings-modern__editor-topbar'>
                <h3>Template Settings</h3>
                <button className='account-settings-modern__btn-secondary' onClick={() => setEditingInvoiceTemplate(null)}>
                  Back
                </button>
              </div>

              <div className='account-settings-modern__form-group'>
                <label>Template Name*</label>
                <input
                  type='text'
                  value={editingInvoiceTemplate.name}
                  onChange={(e) => updateEditingInvoiceTemplate('name', e.target.value)}
                  placeholder='Enter template name'
                />
              </div>

              <div className='account-settings-modern__form-group'>
                <label>Template For</label>
                <Select
                  isMulti
                  isSearchable
                  className='react-select'
                  classNamePrefix='react-select'
                  options={apiSecondaryPlatforms.map((p) => ({ value: String(p.id), label: p.display_name || p.name }))}
                  placeholder='Select channels'
                  value={apiSecondaryPlatforms
                    .filter((p) => editingInvoiceTemplate.channels.includes(String(p.id)))
                    .map((p) => ({ value: String(p.id), label: p.display_name || p.name }))}
                  onChange={(selected) =>
                    updateEditingInvoiceTemplate('channels', (selected as any[]).map((s) => s.value))
                  }
                />
                <p className='account-settings-modern__helper-text'>
                  Select which channels will use this specific template layout.
                </p>
              </div>

              <div className='account-settings-modern__form-group'>
                <label>Language</label>
                <select
                  value={editingInvoiceTemplate.language}
                  onChange={(e) => updateEditingInvoiceTemplate('language', e.target.value)}
                >
                  {apiLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='account-settings-modern__checkbox-row'>
                <span>Include IMEI in Product Line</span>
                <input
                  type='checkbox'
                  checked={editingInvoiceTemplate.includeImei}
                  onChange={(e) => updateEditingInvoiceTemplate('includeImei', e.target.checked)}
                />
              </div>

              <div className='account-settings-modern__checkbox-row'>
                <span>Include Warranty Details</span>
                <input
                  type='checkbox'
                  checked={Boolean(editingInvoiceTemplate.includeWarrantyDetails)}
                  onChange={(e) => updateEditingInvoiceTemplate('includeWarrantyDetails', e.target.checked)}
                />
              </div>

              <div className='account-settings-modern__form-group'>
                <label>Notes of Invoice</label>
                <div className='account-settings-modern__editor-field'>
                  <AppCustomEditor2
                    value={editingInvoiceTemplate.notes}
                    placeholder='Enter invoice notes'
                    onChangeData={(value) => updateEditingInvoiceTemplate('notes', value)}
                  />
                </div>
              </div>

              <div className='account-settings-modern__form-group'>
                <label>Bank Details</label>
                <div className='account-settings-modern__editor-field'>
                  <AppCustomEditor2
                    value={editingInvoiceTemplate.bankDetails}
                    placeholder='Enter bank details'
                    onChangeData={(value) => updateEditingInvoiceTemplate('bankDetails', value)}
                  />
                </div>
              </div>

              <div className='account-settings-modern__form-group'>
                <label>Digital Signature URL</label>
                <input
                  type='text'
                  placeholder='https://...'
                  value={editingInvoiceTemplate.signatureUrl}
                  onChange={(e) => updateEditingInvoiceTemplate('signatureUrl', e.target.value)}
                />
              </div>

              <div className='account-settings-modern__checkbox-row'>
                <span>Set as default template</span>
                <input
                  type='checkbox'
                  checked={editingInvoiceTemplate.isDefault}
                  onChange={(e) => updateEditingInvoiceTemplate('isDefault', e.target.checked)}
                />
              </div>

              <div className='account-settings-modern__button-row'>
                <button className='account-settings-modern__btn-primary' onClick={saveInvoiceTemplate}>
                  {isSavingInvoiceTemplate ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>

            <div className='account-settings-modern__preview-pane'>
              {renderInvoiceTemplatePreview(editingInvoiceTemplate)}
            </div>
          </div>
        )

          case 'template-packing-slip' :
            if (!editingPackingSlipTemplate) {
              return renderPackingSlipTemplateGrid()
            }


        return (
          <>
            <div className='account-settings-modern__editor-layout packing-slip-editor-layout'>
              <div className='account-settings-modern__editor-pane'>
                <div className='account-settings-modern__editor-topbar'>
                <h3>Packing Slip Settings</h3>

                <button
                  className='account-settings-modern__btn-secondary'
                  onClick={closePackingSlipTemplateEditor}
                >
                  Back
                </button>
              </div>

              <div className='account-settings-modern__form-group'>
                <label>Template Name*</label>

                <input
                  type='text'
                  value={editingPackingSlipTemplate.name}
                  onChange={(e) =>
                    updatePackingSlipTemplate('name', e.target.value)
                  }
                  placeholder='Enter template name'
                />
              </div>

              <div className='account-settings-modern__form-group'>
                <label>Template For</label>

                <Select
                  isMulti
                  isSearchable
                  className='react-select'
                  classNamePrefix='react-select'
                  options={apiSecondaryPlatforms.map((p) => ({
                    value: String(p.id),
                    label: p.display_name || p.name,
                  }))}
                  placeholder='Select channels'
                  value={apiSecondaryPlatforms
                    .filter((p) =>
                      editingPackingSlipTemplate.channels.includes(String(p.id))
                    )
                    .map((p) => ({
                      value: String(p.id),
                      label: p.display_name || p.name,
                    }))}
                  onChange={(selected) => {
                    const selectedValues = (selected as any[]).map((option) => option.value)

                    updatePackingSlipTemplate('channels', selectedValues)
                  }}
                />

                <p className='account-settings-modern__helper-text'>
                  Select which sales channels will utilize this document layout.
                </p>
              </div>

              <div className='account-settings-modern__form-group'>
                <PackingSlipTemplateTypeSelector
                  value={editingPackingSlipTemplate.templateKind}
                  onChange={handlePackingSlipTemplateKindChange}
                />
              </div>

              {editingPackingSlipTemplate.templateKind === 'standard' ? (
                <>
                  <div className='account-settings-modern__form-group'>
                    <label>Paper Size Format</label>

                    <select
                      value={editingPackingSlipTemplate.paperSize}
                      onChange={(e) =>
                        updatePackingSlipTemplate('paperSize', e.target.value)
                      }
                    >
                      <option value='4x6'>
                        4&quot; x 6&quot; Thermal Roll Label (Standard)
                      </option>
                      <option value='a4'>
                        A4 Standard Sheet Paper
                      </option>
                      <option value='letter'>
                        8.5&quot; x 11&quot; Letter Sheet
                      </option>
                    </select>
                  </div>

                  <div className='account-settings-modern__form-group'>
                    <label>Display Options</label>

                    {[
                      ['displayBrandLogo', 'Display Brand Logo'],
                      ['displayProductImages', 'Display Product Images in Table'],
                      ['displayShippingMethod', 'Display Shipping Method & Carrier'],
                      ['displayCustomerNotes', 'Display Customer Checkout Notes'],
                      ['displayWarehouseBins', 'Display Warehouse Bin Locations (OMS)'],
                    ].map(([field, label]) => (
                      <div
                        key={field}
                        className='account-settings-modern__checkbox-row'
                      >
                        <span>{label}</span>

                        <input
                          type='checkbox'
                          checked={Boolean(
                            editingPackingSlipTemplate[
                              field as keyof PackingSlipTemplateRecord
                            ]
                          )}
                          onChange={(e) =>
                            updatePackingSlipTemplate(
                              field as keyof PackingSlipTemplateRecord,
                              e.target.checked
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <PackingSlipCustomSettings
                  template={editingPackingSlipTemplate}
                  onChange={(patch) =>
                    setEditingPackingSlipTemplate((prev) =>
                      prev ? {...prev, ...patch} : prev
                    )
                  }
                />
              )}

              <div className='account-settings-modern__checkbox-row'>
                <span>Set as default template</span>
                <input
                  type='checkbox'
                  checked={editingPackingSlipTemplate.isDefault}
                  onChange={(e) =>
                    updatePackingSlipTemplate('isDefault', e.target.checked)
                  }
                />
              </div>

              <div className='account-settings-modern__button-row'>
                <button
                  className='account-settings-modern__btn-primary w-100'
                  onClick={savePackingSlipTemplate}
                  disabled={isSavingPackingSlipTemplate}
                >
                  {isSavingPackingSlipTemplate
                    ? 'Saving...'
                    : 'Save Template Changes'}
                </button>
              </div>
            </div>

            <div
              className={`account-settings-modern__preview-pane packing-slip-preview-pane ${
                editingPackingSlipTemplate.templateKind === 'custom'
                  ? 'packing-slip-preview-pane--custom-workspace'
                  : ''
              }`}
            >
              {editingPackingSlipTemplate.templateKind === 'standard' ? (
                <>
                  <div className='packing-slip-preview__heading'>
                    <span>
                      LIVE PRINT PREVIEW (
                      {(
                        PACKING_SLIP_PAGE_FORMATS[
                          editingPackingSlipTemplate.paperSize as keyof typeof PACKING_SLIP_PAGE_FORMATS
                        ] ?? PACKING_SLIP_PAGE_FORMATS['4x6']
                      ).previewLabel}
                      )
                    </span>
                    <button
                      type='button'
                      className='packing-slip-preview__zoom'
                    >
                      Zoom 100%
                    </button>
                  </div>

                  <div className='packing-slip-preview-stage'>
                    {renderPackingSlipPreview(editingPackingSlipTemplate)}
                  </div>
                </>
              ) : (
                <PackingSlipCustomWorkspace
                  key={editingPackingSlipTemplate.id}
                  templateHtml={editingPackingSlipTemplate.templateHtml ?? ''}
                  onTemplateHtmlChange={(html) =>
                    updatePackingSlipTemplate('templateHtml', html)
                  }
                  platformIds={editingPackingSlipTemplate.channels}
                  paperSize={editingPackingSlipTemplate.paperSize}
                  onPaperSizeChange={(paperSize) =>
                    updatePackingSlipTemplate('paperSize', paperSize)
                  }
                  orientation={editingPackingSlipTemplate.orientation}
                  marginMm={editingPackingSlipTemplate.marginMm}
                  blockSavingUnknownSourceCode={
                    editingPackingSlipTemplate.blockSavingUnknownSourceCode ?? true
                  }
                  onShortcodeValidationChange={setCustomPackingSlipShortcodeValidation}
                  isNewCustomTemplate={String(
                    editingPackingSlipTemplate.id
                  ).startsWith('new-packing-slip-')}
                />
              )}
            </div>
            </div>

            <CommonAlertModal
              title={packingSlipKindSwitchPrompt?.title ?? 'Change template type?'}
              subtitle={packingSlipKindSwitchPrompt?.message ?? ''}
              isVisible={Boolean(packingSlipKindSwitchPrompt)}
              onSuccess={confirmPackingSlipKindSwitch}
              onCancel={cancelPackingSlipKindSwitch}
              cancelBtnTitle='Cancel'
              successBtnTitle='Continue'
              successBtnClasses='btn btn-sm btn-primary m-2'
              isTermsVisible={false}
            />
          </>
        )

      default:
        return null
    }
  }

  return (
    <div
      className={`account-settings-modern ${
        activeSection === 'template-packing-slip' ? 'is-packing-slip' : ''
      }`}
    >
      <div className='account-settings-modern__brand-logo-well'>
        <button
          type='button'
          className='account-settings-modern__brand-logo-button'
          onClick={() => navigate('/dashboard')}
          aria-label='Go to dashboard'
        >
          <img
            alt='Logo'
            src={toAbsoluteUrl('/media/logos/commercium-logo-light.png')}
            className='h-50px m-2.5 app-sidebar-logo-default'
          />
        </button>
      </div>

      <header className='account-settings-modern__brand-header'>
        <div className='account-settings-modern__page-user'>
          <span className='account-settings-modern__page-user-label'>Logged in as</span>
          <span
            className='account-settings-modern__page-user-name'
            title={settingsLoggedInUserName}
          >
            {settingsLoggedInUserName}
          </span>
        </div>
        <div className='account-settings-modern__page-navbar'>
          <Navbar />
        </div>
      </header>

      <aside className='account-settings-modern__menu'>
        <div className='account-settings-modern__menu-top'>
          <button
            type='button'
            className='account-settings-modern__back-dashboard'
            onClick={goToDashboard}
          >
            <KTIcon iconName='arrow-left' />
            <span>Back to Dashboard</span>
          </button>
        </div>
        <div className='account-settings-modern__menu-scroll'>
          {menuGroups.map((group) => {
            const visibleItems = group.items.filter(
              (item) =>
                (item.key !== 'company-warehouses' || hasOmsPlatform) &&
                (item.key !== 'my-account-application-logs' || canAccessApplicationLogs)
            )
            if (!visibleItems.length) return null
            return (
              <div className='account-settings-modern__menu-group' key={group.title}>
                <h6>{group.title}</h6>
                {visibleItems.map((item) => (
                  <button
                    key={item.key}
                    className={`account-settings-modern__menu-item ${activeSection === item.key ? 'is-active' : ''}`}
                    onClick={() => setActiveSection(item.key)}
                    type='button'
                    aria-current={activeSection === item.key ? 'page' : undefined}
                  >
                    <KTIcon iconName={item.icon} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            )
          })}
        </div>
      </aside>

      <section className='account-settings-modern__content'>
        <div className='account-settings-modern__page-chrome'>
          <nav className='account-settings-modern__breadcrumb' aria-label='Settings breadcrumb'>
            <ol>
              {settingsBreadcrumbItems.map((crumb, index) => {
                const isLast = index === settingsBreadcrumbItems.length - 1
                return (
                  <li key={`${crumb.label}-${index}`}>
                    {index > 0 ? (
                      <span className='account-settings-modern__breadcrumb-sep' aria-hidden='true'>
                        {'>'}
                      </span>
                    ) : null}
                    {isLast || !crumb.onSelect ? (
                      <span className='account-settings-modern__breadcrumb-current' aria-current='page'>
                        {crumb.label}
                      </span>
                    ) : (
                      <button
                        type='button'
                        className='account-settings-modern__breadcrumb-link'
                        onClick={crumb.onSelect}
                      >
                        {crumb.label}
                      </button>
                    )}
                  </li>
                )
              })}
            </ol>
          </nav>
          <h1 className='account-settings-modern__page-heading fw-bold text-dark'>
            {activeMenuItem.pageTitle}
          </h1>
        </div>
        {renderContent()}
      </section>

      {/* 2FA Setup Modal */}
      {qrLoading ? (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1050 }}>
          <Loader variant="section" />
        </div>
      ) : (
      <Modal centered={hasMfaQrCode ? false : true} backdrop='static' show={showMfaSetupModal} onHide={() => setShowMfaSetupModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>🔐 2-Factor Authentication {hasMfaQrCode ? 'Setup' : null}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card.Body>
            <div className='mb-4'>
              {hasMfaQrCode ? (
                <>
                  <h5 className='fw-semibold mb-2'>1. Install a compatible app</h5>
                  <p className='text-muted small mb-1'>
                    Install an authenticator app such as <strong>Google Authenticator</strong>,{' '}
                    <strong>Microsoft Authenticator</strong> on your phone or computer.
                  </p>
                </>
              ) : (
                <Alert variant='success'>
                  Note: You've already configured 2-Factor Authentication for your account. To enable
                  2-Factor Authentication enter code from authenticator app below.
                </Alert>
              )}
            </div>

          {hasMfaQrCode && (
            <div className='mb-4'>
              <h5 className='fw-semibold mb-2'>2. Scan the QR Code</h5>
              <div className='text-center d-flex align-items-center justify-content-center'>
                <img
                  src={qrCodeUrl}
                  alt='QR Code'
                  className='border rounded-3 p-2 shadow-sm my-2'
                  style={{ width: '200px', height: '200px' }}
                />
              </div>
              <p className='text-muted small'>
                Open your authenticator app, choose <strong>Scan QR code</strong>, and scan the
                image above.
              </p>
            </div>
          )}

          <Formik initialValues={{ mfaCode: '' }} validationSchema={MfaSchema} onSubmit={(values, { setSubmitting }) => { verifyMfaEnable(values); setSubmitting(false) }}>
            {({ isSubmitting }) => (
              <FormikForm>
                <div className='mb-3'>
                  <h5 className='fw-semibold mb-2'>{hasMfaQrCode ? '3.' : ''} Enter 2-Factor Authentication code</h5>
                  <Form.Group className='mb-3' controlId='mfaCode'>
                    <Field as={Form.Control} type='text' name='mfaCode' placeholder='Enter Authentication Code' className='rounded-3' />
                    <ErrorMessage name='mfaCode' component='div' className='text-danger mt-1' />
                  </Form.Group>
                  <Button type='submit' variant='primary' className='w-100 rounded-3 fw-semibold' style={{ padding: '10px 0' }} disabled={isSubmitting || isUpdatingMfa}>
                    Verify & Continue
                  </Button>
                </div>
              </FormikForm>
            )}
          </Formik>
          </Card.Body>
        </Modal.Body>
      </Modal>
      )}

      {/* 2FA Disable Modal */}
      <Modal centered backdrop='static' show={showDisableMfaModal} onHide={() => setShowDisableMfaModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Disable 2-Factor Authentication</Modal.Title>
        </Modal.Header>
        <Formik initialValues={{ disablecode: '' }} validationSchema={DisableMfaSchema} onSubmit={(values, { setSubmitting }) => { verifyMfaDisable(values); setSubmitting(false) }}>
          {({ isSubmitting }) => (
            <FormikForm>
              <Modal.Body>
                <Alert variant='danger'>
                  Warning: Disabling 2-Factor Authentication will remove an extra layer of
                  protection from your account. Only proceed if you understand the risks.
                </Alert>
                <p>
                  For security reasons, disabling 2-Factor Authentication requires authentication
                  code verification. Please enter the authentication code from your authenticator
                  app to continue.
                </p>
                <Form.Group controlId='otpInput'>
                  <Form.Label>Enter authentication code</Form.Label>
                  <Field name='disablecode' as={Form.Control} type='text' placeholder='Enter authentication code' />
                  <ErrorMessage name='disablecode' component='div' className='text-danger mt-1' />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={() => setShowDisableMfaModal(false)}>Cancel</Button>
                <Button variant='danger' type='submit' disabled={isSubmitting || isUpdatingMfa}>Verify & Disable</Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>
    </div>
  )
}

export default AccountSettingsModern
